import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { 
  AlertTriangle, 
  Trash2, 
  RefreshCw, 
  Database,
  Users,
  GraduationCap,
  BookOpen,
  Calendar,
  DollarSign,
  Loader2,
  ShieldAlert,
  History,
  Search,
  UserSearch,
  Eye,
  Clock,
  CheckCircle2,
  XCircle,
  UserMinus
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLLECTION_GROUPS = {
  "Academic": ["students", "teachers", "staff", "classes", "sections", "subjects", "promotion_history"],
  "Finance": ["fees", "fee_heads", "fee_invoices", "fee_payments", "fee_structures", "fee_discounts", "student_fee_assignments", "salary", "salary_slips"],
  "Accounting": ["chart_of_accounts", "journal_entries", "accounts_transactions", "income_entries", "expense_entries", "budget_allocations", "bank_accounts", "bank_transactions", "petty_cash", "assets", "liabilities"],
  "Attendance": ["attendance", "staff_attendance"],
  "Exams": ["exams", "exam_results", "marks"],
  "Others": ["homework", "notices", "announcements", "timetable", "documents", "certificates", "behavior", "id_cards", "gate_passes"],
  "Facilities": ["hostel", "hostel_students", "transport", "transport_students", "library", "library_issues"],
  "Inventory": ["inventory_categories", "inventory_items", "inventory_stock", "stock_issues", "stock_damages"],
  "Suppliers": ["suppliers", "supplier_purchases", "supplier_payments"],
  "Store": ["store_items", "store_sales"],
  "Users": ["users"]
};

const TenantResetManager = ({ schools = [] }) => {
  const { getAuthHeader, user } = useAuth();
  const [selectedSchool, setSelectedSchool] = useState('');
  const [loading, setLoading] = useState(false);
  const [dataSummary, setDataSummary] = useState(null);
  const [resetType, setResetType] = useState('partial');
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [resetHistory, setResetHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  
  // Find Students state
  const [showFindStudents, setShowFindStudents] = useState(false);
  const [classSummary, setClassSummary] = useState(null);
  const [loadingClassSummary, setLoadingClassSummary] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchingStudents, setSearchingStudents] = useState(false);
  
  // Move Students state
  const [schoolClasses, setSchoolClasses] = useState([]);
  const [targetClassId, setTargetClassId] = useState('');
  const [admissionNumbers, setAdmissionNumbers] = useState('');
  const [movingStudents, setMovingStudents] = useState(false);
  const [moveResults, setMoveResults] = useState(null);
  
  // Delete Payment state
  const [deletePaymentId, setDeletePaymentId] = useState('');
  const [deletePaymentPreview, setDeletePaymentPreview] = useState(null);
  const [deletingPayment, setDeletingPayment] = useState(false);
  
  // Delete Single Student state
  const [deleteStudentId, setDeleteStudentId] = useState('');
  const [deleteStudentPreview, setDeleteStudentPreview] = useState(null);
  const [deletingStudent, setDeletingStudent] = useState(false);
  
  // Promotion Reset state
  const [resettingPromotions, setResettingPromotions] = useState(false);
  const [fixingClasses, setFixingClasses] = useState(false);
  const [resetAcademicYear, setResetAcademicYear] = useState('2026-2027');
  
  // Emergency Fix state
  const [emergencyFixNumbers, setEmergencyFixNumbers] = useState('');
  const [emergencyFixClassId, setEmergencyFixClassId] = useState('');
  const [emergencyFixing, setEmergencyFixing] = useState(false);

  // Delete Tenant state
  const [deleteTenantPreview, setDeleteTenantPreview] = useState(null);
  const [deletingTenant, setDeletingTenant] = useState(false);
  const [deleteTenantConfirmText, setDeleteTenantConfirmText] = useState('');
  const [emergencyFixResult, setEmergencyFixResult] = useState(null);
  
  // Demote All state
  const [demotingAll, setDemotingAll] = useState(false);
  const [demoteResult, setDemoteResult] = useState(null);
  
  // Restore from Excel state
  const [restoringFromExcel, setRestoringFromExcel] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  
  // Import/Delete Students state
  const [deletingAllStudents, setDeletingAllStudents] = useState(false);
  const [importingStudents, setImportingStudents] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Delete Students by Class state
  const [deleteByClassId, setDeleteByClassId] = useState('');
  const [deleteByClassPreview, setDeleteByClassPreview] = useState(null);
  const [deletingByClass, setDeletingByClass] = useState(false);
  
  // Generate Invoices state
  const [generatingInvoices, setGeneratingInvoices] = useState(false);
  const [invoiceResult, setInvoiceResult] = useState(null);
  const [deletingInvoices, setDeletingInvoices] = useState(false);
  
  // Update Student Details state
  const [updatingStudentDetails, setUpdatingStudentDetails] = useState(false);
  const [updateDetailsResult, setUpdateDetailsResult] = useState(null);
  
  // Accounting Cleanup state
  const [accountingSummary, setAccountingSummary] = useState(null);
  const [loadingAccountingSummary, setLoadingAccountingSummary] = useState(false);
  const [deletingAccountingData, setDeletingAccountingData] = useState(false);
  const [accountingDeleteType, setAccountingDeleteType] = useState('test');
  
  // Wrong Invoice Cleanup state
  const [wrongInvoicePreview, setWrongInvoicePreview] = useState(null);
  const [loadingWrongInvoices, setLoadingWrongInvoices] = useState(false);
  const [deletingWrongInvoices, setDeletingWrongInvoices] = useState(false);
  const [wrongInvoiceDateFilter, setWrongInvoiceDateFilter] = useState('');

  // Cron Duplicate Cleanup state
  const [cronDupPreview, setCronDupPreview] = useState(null);
  const [loadingCronDup, setLoadingCronDup] = useState(false);
  const [deletingCronDup, setDeletingCronDup] = useState(false);
  const [cronDupMonth, setCronDupMonth] = useState('');

  // One-Time Fee Duplicate Cleanup state
  const [onetimeDupPreview, setOnetimeDupPreview] = useState(null);
  const [loadingOnetimeDup, setLoadingOnetimeDup] = useState(false);
  const [deletingOnetimeDup, setDeletingOnetimeDup] = useState(false);

  // Wrong Admission Fee Cleanup state
  const [wrongAdmPreview, setWrongAdmPreview] = useState(null);
  const [loadingWrongAdm, setLoadingWrongAdm] = useState(false);
  const [deletingWrongAdm, setDeletingWrongAdm] = useState(false);

  // Fractional amount cleanup state
  const [fracScan, setFracScan] = useState(null);
  const [loadingFracScan, setLoadingFracScan] = useState(false);
  const [fixingFrac, setFixingFrac] = useState(false);
  const [fracStudentId, setFracStudentId] = useState('');

  // Duplicate one-time invoice cleanup (Session/Dev/Exam etc) state
  const [dupOneTimePreview, setDupOneTimePreview] = useState(null);
  const [loadingDupOneTime, setLoadingDupOneTime] = useState(false);
  const [deletingDupOneTime, setDeletingDupOneTime] = useState(false);
  const [dupOneTimeStudentId, setDupOneTimeStudentId] = useState('');

  // Background Job state
  const [activeJob, setActiveJob] = useState(null);
  const [jobProgress, setJobProgress] = useState(0);
  const [jobMessage, setJobMessage] = useState('');
  const pollRef = useRef(null);

  // Define fetchDataSummary first since it's used in pollJobStatus
  const fetchDataSummary = useCallback(async (schoolId) => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/super-admin/tenant/${schoolId}/data-summary`,
        { headers: getAuthHeader() }
      );
      setDataSummary(res.data);
    } catch (error) {
      toast.error('Failed to fetch data summary');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  // Define fetchClassSummary 
  const fetchClassSummary = useCallback(async () => {
    if (!selectedSchool) return;
    setLoadingClassSummary(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/class-summary`,
        { headers: getAuthHeader() }
      );
      setClassSummary(res.data);
      setShowFindStudents(true);
    } catch (error) {
      toast.error('Failed to fetch class summary');
    } finally {
      setLoadingClassSummary(false);
    }
  }, [selectedSchool, getAuthHeader]);

  // Poll job status
  const pollJobStatus = useCallback(async (jobId, onComplete) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/jobs/${jobId}/status`,
        { headers: getAuthHeader() }
      );
      
      const job = res.data;
      setJobProgress(job.progress || 0);
      setJobMessage(job.message || '');
      
      if (job.status === 'completed') {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
        setActiveJob(null);
        toast.success(job.message || 'Operation completed!');
        if (onComplete) onComplete(job.result);
        fetchClassSummary();
        fetchDataSummary(selectedSchool);
      } else if (job.status === 'failed') {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
        setActiveJob(null);
        toast.error(job.error || 'Operation failed');
      }
    } catch (err) {
      console.error('Error polling job status:', err);
    }
  }, [getAuthHeader, selectedSchool, fetchClassSummary, fetchDataSummary]);

  // Start a background job
  const startBackgroundJob = useCallback(async (endpoint, data, jobType, onComplete) => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    
    try {
      const res = await axios.post(
        `${API_URL}${endpoint}`,
        data || {},
        { headers: getAuthHeader() }
      );
      
      const jobId = res.data.job_id;
      setActiveJob({ id: jobId, type: jobType });
      setJobProgress(0);
      setJobMessage('Starting...');
      
      // Start polling
      pollRef.current = setInterval(() => {
        pollJobStatus(jobId, onComplete);
      }, 2000);
      
      // Initial poll
      await pollJobStatus(jobId, onComplete);
      
      return jobId;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start job');
      throw err;
    }
  }, [selectedSchool, getAuthHeader, pollJobStatus]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, []);

  // Only super admin can use this
  if (user?.role !== 'super_admin') {
    return null;
  }

  // Recalculate student fees
  const recalculateStudentFees = async () => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    
    setRecalculating(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/recalculate-student-fees`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message);
      fetchDataSummary(selectedSchool);
    } catch (error) {
      toast.error('Failed to recalculate fees');
    } finally {
      setRecalculating(false);
    }
  };

  // Fetch class summary (student count per class)
  // Search students by name
  const searchStudents = async () => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    
    if (!searchName.trim()) {
      toast.error('Please enter a student name to search');
      return;
    }
    
    setSearchingStudents(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/find-students?name=${encodeURIComponent(searchName)}`,
        { headers: getAuthHeader() }
      );
      setSearchResults(res.data);
      if (res.data.total_found === 0) {
        toast.info('No students found with that name');
      } else {
        toast.success(`Found ${res.data.total_found} student(s)`);
      }
    } catch (error) {
      toast.error('Failed to search students');
    } finally {
      setSearchingStudents(false);
    }
  };

  // Search all students (no filter)
  const searchAllStudents = async (classId = null, status = null) => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    
    setSearchingStudents(true);
    try {
      let url = `${API_URL}/api/super-admin/tenant/${selectedSchool}/find-students?`;
      if (classId) url += `class_id=${classId}&`;
      if (status) url += `promotion_status=${status}&`;
      
      const res = await axios.get(url, { headers: getAuthHeader() });
      setSearchResults(res.data);
      toast.success(`Found ${res.data.total_found} student(s)`);
    } catch (error) {
      toast.error('Failed to fetch students');
    } finally {
      setSearchingStudents(false);
    }
  };

  // Fetch classes for the selected school
  const fetchSchoolClasses = async (schoolId) => {
    const targetSchoolId = schoolId || selectedSchool;
    if (!targetSchoolId) return;
    try {
      const res = await axios.get(
        `${API_URL}/api/super-admin/tenant/${targetSchoolId}/classes`,
        { headers: getAuthHeader() }
      );
      setSchoolClasses(res.data.classes || []);
    } catch (error) {
      console.error('Failed to fetch classes:', error);
      setSchoolClasses([]);
    }
  };

  // Move students to a different class
  const moveStudentsToClass = async () => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    if (!targetClassId) {
      toast.error('Please select a target class');
      return;
    }
    if (!admissionNumbers.trim()) {
      toast.error('Please enter admission numbers');
      return;
    }
    
    const admNumbers = admissionNumbers.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
    if (admNumbers.length === 0) {
      toast.error('No valid admission numbers found');
      return;
    }
    
    if (!window.confirm(`Move ${admNumbers.length} student(s) to the selected class?`)) {
      return;
    }
    
    setMovingStudents(true);
    setMoveResults(null);
    try {
      const params = new URLSearchParams();
      params.append('target_class_id', targetClassId);
      admNumbers.forEach(adm => params.append('student_admission_numbers', adm));
      
      const res = await axios.post(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/move-students-to-class?${params.toString()}`,
        {},
        { headers: getAuthHeader() }
      );
      setMoveResults(res.data);
      toast.success(res.data.message);
      
      // Refresh class summary
      fetchClassSummary();
    } catch (error) {
      toast.error('Failed to move students: ' + (error.response?.data?.detail || error.message));
    } finally {
      setMovingStudents(false);
    }
  };

  // Preview delete students by class
  const previewDeleteByClass = async () => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    if (!deleteByClassId) {
      toast.error('Please select a class');
      return;
    }
    
    setDeletingByClass(true);
    setDeleteByClassPreview(null);
    try {
      const res = await axios.delete(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/delete-students-by-class?class_id=${deleteByClassId}&preview=true`,
        { headers: getAuthHeader() }
      );
      setDeleteByClassPreview(res.data);
    } catch (error) {
      toast.error('Failed to preview: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeletingByClass(false);
    }
  };

  // Execute delete students by class
  const executeDeleteByClass = async () => {
    if (!selectedSchool || !deleteByClassId) return;
    
    const className = deleteByClassPreview?.class_name || 'this class';
    const count = deleteByClassPreview?.student_count || 0;
    
    if (!window.confirm(`⚠️ DANGER! Delete ${count} students from ${className}?\n\nThis will also delete:\n- All their fee invoices\n- All their payments\n- All their promotion history\n\nThis action is IRREVERSIBLE!`)) {
      return;
    }
    
    setDeletingByClass(true);
    try {
      const res = await axios.delete(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/delete-students-by-class?class_id=${deleteByClassId}&preview=false&confirm=true`,
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message);
      setDeleteByClassPreview(null);
      setDeleteByClassId('');
      fetchClassSummary();
      fetchDataSummary(selectedSchool);
    } catch (error) {
      toast.error('Failed to delete: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeletingByClass(false);
    }
  };

  // Preview delete payment
  const previewDeletePayment = async () => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    if (!deletePaymentId.trim()) {
      toast.error('Please enter Payment ID or Receipt Number');
      return;
    }
    
    setDeletingPayment(true);
    setDeletePaymentPreview(null);
    try {
      // Use the input as-is - backend now supports both payment ID and receipt number
      const paymentIdOrReceipt = encodeURIComponent(deletePaymentId.trim());
      
      const res = await axios.delete(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/delete-payment/${paymentIdOrReceipt}?confirm=false`,
        { headers: getAuthHeader() }
      );
      // Store the actual payment ID from response for deletion
      setDeletePaymentPreview({ ...res.data, paymentId: res.data.payment?.id || deletePaymentId.trim() });
    } catch (error) {
      toast.error('Failed to preview: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeletingPayment(false);
    }
  };

  // Execute delete payment
  const executeDeletePayment = async () => {
    if (!selectedSchool || !deletePaymentPreview?.paymentId) return;
    
    const receipt = deletePaymentPreview?.payment?.receipt_number || 'this payment';
    const amount = deletePaymentPreview?.payment?.amount || 0;
    
    if (!window.confirm(`⚠️ DANGER! Delete payment ${receipt} (₹${amount})?\n\nThis will also delete:\n- Related journal entries\n- Related bank/cash entries\n\nThis action is IRREVERSIBLE!`)) {
      return;
    }
    
    setDeletingPayment(true);
    try {
      const res = await axios.delete(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/delete-payment/${deletePaymentPreview.paymentId}?confirm=true`,
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message);
      setDeletePaymentPreview(null);
      setDeletePaymentId('');
      fetchDataSummary(selectedSchool);
    } catch (error) {
      toast.error('Failed to delete: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeletingPayment(false);
    }
  };

  // Preview delete single student
  const previewDeleteStudent = async () => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    if (!deleteStudentId.trim()) {
      toast.error('Please enter Student ID or Admission Number');
      return;
    }
    
    setDeletingStudent(true);
    setDeleteStudentPreview(null);
    try {
      let studentId = deleteStudentId.trim();
      
      // If it doesn't look like a UUID, search by admission number
      if (!studentId.includes('-') || studentId.length < 30) {
        // Search for student by admission number
        const searchRes = await axios.get(
          `${API_URL}/api/super-admin/tenant/${selectedSchool}/find-students?admission_number=${encodeURIComponent(studentId)}`,
          { headers: getAuthHeader() }
        );
        const found = searchRes.data.students?.[0];
        if (found) {
          studentId = found.id;
        } else {
          toast.error('Student with this admission number not found');
          setDeletingStudent(false);
          return;
        }
      }
      
      const res = await axios.delete(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/delete-student/${studentId}?confirm=false`,
        { headers: getAuthHeader() }
      );
      setDeleteStudentPreview({ ...res.data, studentId });
    } catch (error) {
      toast.error('Failed to preview: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeletingStudent(false);
    }
  };

  // Execute delete single student
  const executeDeleteStudent = async () => {
    if (!selectedSchool || !deleteStudentPreview?.studentId) return;
    
    const name = deleteStudentPreview?.student?.name || 'this student';
    const admNo = deleteStudentPreview?.student?.admission_number || '';
    
    if (!window.confirm(`⚠️ DANGER! Delete student "${name}" (${admNo})?\n\nThis will also delete:\n- All their fee invoices (${deleteStudentPreview.related_data?.invoices || 0})\n- All their payments (${deleteStudentPreview.related_data?.payments || 0})\n- Total paid: ₹${deleteStudentPreview.related_data?.total_amount_paid?.toLocaleString() || 0}\n\nThis action is IRREVERSIBLE!`)) {
      return;
    }
    
    setDeletingStudent(true);
    try {
      const res = await axios.delete(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/delete-student/${deleteStudentPreview.studentId}?confirm=true`,
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message);
      setDeleteStudentPreview(null);
      setDeleteStudentId('');
      fetchClassSummary();
      fetchDataSummary(selectedSchool);
    } catch (error) {
      toast.error('Failed to delete: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeletingStudent(false);
    }
  };

  // Reset all promotions for an academic year
  const resetAllPromotions = async () => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    
    if (!resetAcademicYear) {
      toast.error('Please enter academic year to reset');
      return;
    }
    
    if (!window.confirm(`⚠️ DANGER! This will:\n\n1. Revert ALL students to their ORIGINAL class before promotion\n2. DELETE all invoices for ${resetAcademicYear}\n3. DELETE all fee assignments for ${resetAcademicYear}\n\nAre you ABSOLUTELY sure?`)) {
      return;
    }
    
    setResettingPromotions(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/reset-all-promotions?academic_year=${encodeURIComponent(resetAcademicYear)}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message);
      fetchDataSummary(selectedSchool);
      fetchClassSummary();
    } catch (error) {
      toast.error('Failed to reset promotions: ' + (error.response?.data?.detail || error.message));
    } finally {
      setResettingPromotions(false);
    }
  };

  // Fix student classes from promotion history
  const fixStudentClasses = async () => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    
    if (!window.confirm('This will check all students and fix their class based on promotion history. Students who were promoted but their class was not updated will be fixed. Continue?')) {
      return;
    }
    
    setFixingClasses(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/fix-student-classes`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message);
      
      if (res.data.issues && res.data.issues.length > 0) {
        console.log('Fixed students:', res.data.issues);
        toast.info(`Fixed ${res.data.fixed} students. Check console for details.`);
      }
      
      fetchClassSummary();
    } catch (error) {
      toast.error('Failed to fix classes: ' + (error.response?.data?.detail || error.message));
    } finally {
      setFixingClasses(false);
    }
  };

  // Emergency Fix - Move students to correct class and clean ALL their data
  const emergencyFix = async () => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    if (!emergencyFixClassId) {
      toast.error('Please select the CORRECT class');
      return;
    }
    if (!emergencyFixNumbers.trim()) {
      toast.error('Please enter admission numbers');
      return;
    }
    
    const admNumbers = emergencyFixNumbers.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
    
    if (!window.confirm(`⚠️ EMERGENCY FIX for ${admNumbers.length} students:\n\n1. Move them to selected class\n2. DELETE all their invoices\n3. DELETE all their fee assignments\n4. DELETE all promotion history\n\nThis gives them a CLEAN START. Continue?`)) {
      return;
    }
    
    setEmergencyFixing(true);
    setEmergencyFixResult(null);
    try {
      const params = new URLSearchParams();
      params.append('correct_class_id', emergencyFixClassId);
      params.append('delete_invoices', 'true');
      admNumbers.forEach(adm => params.append('student_admission_numbers', adm));
      
      const res = await axios.post(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/emergency-class-fix?${params.toString()}`,
        {},
        { headers: getAuthHeader() }
      );
      setEmergencyFixResult(res.data);
      toast.success(res.data.message);
      fetchClassSummary();
    } catch (error) {
      toast.error('Emergency fix failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setEmergencyFixing(false);
    }
  };

  // DELETE TENANT - Preview and Execute
  const previewDeleteTenant = async () => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    setDeletingTenant(true);
    setDeleteTenantPreview(null);
    setDeleteTenantConfirmText('');
    try {
      const res = await axios.delete(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/delete-tenant?confirm=false`,
        { headers: getAuthHeader() }
      );
      setDeleteTenantPreview(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to preview');
    } finally {
      setDeletingTenant(false);
    }
  };

  const executeDeleteTenant = async () => {
    if (!selectedSchool || !deleteTenantPreview) return;
    const schoolName = deleteTenantPreview.school?.name || '';
    if (deleteTenantConfirmText !== schoolName) {
      toast.error(`Type the exact school name "${schoolName}" to confirm`);
      return;
    }
    if (!window.confirm(`FINAL WARNING: Permanently delete "${schoolName}" and ALL its data? This cannot be undone!`)) {
      return;
    }
    setDeletingTenant(true);
    try {
      const res = await axios.delete(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/delete-tenant?confirm=true`,
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message);
      setDeleteTenantPreview(null);
      setDeleteTenantConfirmText('');
      setSelectedSchool('');
      // Reload page to refresh school list
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tenant');
    } finally {
      setDeletingTenant(false);
    }
  };

  // DEMOTE ALL - Move all students back one class - Using Background Job
  const demoteAllOneClass = async () => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    
    if (!window.confirm(`⚠️ এটা সব student কে এক class পিছিয়ে দেবে:\n\nClass 12 → Class 11\nClass 11 → Class 10\nClass 10 → Class 9\n... এভাবে সবাই\n\nএছাড়াও:\n- সব 2026-2027 এর invoices delete হবে\n- সব promotion history delete হবে\n\nআপনি কি নিশ্চিত?`)) {
      return;
    }
    
    setDemotingAll(true);
    setDemoteResult(null);
    try {
      // Try background job first
      await startBackgroundJob(
        `/api/jobs/demote-all/${selectedSchool}`,
        null,
        'demote_all',
        (result) => {
          setDemotingAll(false);
          setDemoteResult(result);
        }
      );
    } catch (error) {
      // Fallback to direct API call
      try {
        const res = await axios.post(
          `${API_URL}/api/super-admin/tenant/${selectedSchool}/demote-all-one-class`,
          {},
          { headers: getAuthHeader() }
        );
        setDemoteResult(res.data);
        toast.success(res.data.message);
        fetchClassSummary();
        fetchDataSummary(selectedSchool);
        setDemotingAll(false);
      } catch (err) {
        toast.error('Failed to demote: ' + (err.response?.data?.detail || err.message));
        setDemotingAll(false);
      }
    }
  };

  // Restore classes from Excel data (hardcoded from user's files)
  const restoreFromExcelData = async () => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    
    // This is the data extracted from user's Excel files
    const classStudentMapping = {
      "UKG": [
        {"admission_number": "142425", "name": "AAROHI SAHA"},
        {"admission_number": "472526", "name": "AAYAN KAMI"},
        {"admission_number": "422526", "name": "ADITI MUNDA"},
        {"admission_number": "092425", "name": "ADRISHA BHOWMIK"},
        {"admission_number": "232324", "name": "ADWITIYA DAS"},
        {"admission_number": "312425", "name": "AHONA SARKAR"},
        {"admission_number": "412324", "name": "AKASH ROY"},
        {"admission_number": "112425", "name": "ANISHA DAS"},
        {"admission_number": "512526", "name": "ANWESHA TOPPO"},
        {"admission_number": "392324", "name": "ARINJOY SARKAR"},
        {"admission_number": "092526", "name": "ARITRA BARMAN"},
        {"admission_number": "502526", "name": "ARMAN SK"},
        {"admission_number": "372526", "name": "AROHI RAY"},
        {"admission_number": "102425", "name": "AYUSH BARMAN"},
        {"admission_number": "152526", "name": "BIDIPTA SARKAR"},
        {"admission_number": "262526", "name": "DEBJANI SAHA"},
        {"admission_number": "332526", "name": "DISHA BARAI"},
        {"admission_number": "352526", "name": "ISHIKA LAKRA"},
        {"admission_number": "222526", "name": "JINIA SARKAR"},
        {"admission_number": "052526", "name": "JISAN SK"},
        {"admission_number": "532526", "name": "JOYEETA CHOUDHURY"},
        {"admission_number": "012526", "name": "KAVERI LOHAR"},
        {"admission_number": "402526", "name": "KINJAL SARKAR"},
        {"admission_number": "132526", "name": "LIYANSH GHOSH"},
        {"admission_number": "302526", "name": "MAYUKH DEY"},
        {"admission_number": "442526", "name": "MEGHNA SHA"},
        {"admission_number": "212425", "name": "MOU SARKAR"},
        {"admission_number": "362526", "name": "NOYONIKA ROY"},
        {"admission_number": "242526", "name": "PRATYUSH GHOSH"},
        {"admission_number": "122526", "name": "PRIYANSHU SARKAR"},
        {"admission_number": "452526", "name": "REYAANSH ORAON"},
        {"admission_number": "192526", "name": "RIYANSH RAY"},
        {"admission_number": "062526", "name": "RUDRA SARKAR"},
        {"admission_number": "162526", "name": "RUPSHA SARKAR"},
        {"admission_number": "022526", "name": "RYAN BARMAN"},
        {"admission_number": "272526", "name": "SAYAN BARMAN"},
        {"admission_number": "172526", "name": "SHIVAM SARKAR"},
        {"admission_number": "382526", "name": "SHREYA GUPTA"},
        {"admission_number": "202526", "name": "SOUMYAJIT DAS"},
        {"admission_number": "282526", "name": "TANISHA KHARIA"},
        {"admission_number": "072526", "name": "UTSAB BARMAN"}
      ],
      "Class 1": [
        {"admission_number": "422324", "name": "AAHAN DAS"},
        {"admission_number": "1000", "name": "ABU BAKKAR SIDDIK"},
        {"admission_number": "432526", "name": "ADRIT MODAK"},
        {"admission_number": "112324", "name": "AGNIV ROY"},
        {"admission_number": "1001", "name": "AMBIKA ROY"},
        {"admission_number": "462526", "name": "ANGEL MUNDA"},
        {"admission_number": "342425", "name": "ANUSHKA ROY"},
        {"admission_number": "032425", "name": "ARCHI SAHA"},
        {"admission_number": "352425", "name": "ARIBA KHATUN"},
        {"admission_number": "242425", "name": "ARIJIT MANDAL"},
        {"admission_number": "322425", "name": "AROHI DAS"},
        {"admission_number": "1002", "name": "ARSHAN MD"},
        {"admission_number": "042425", "name": "ARYA SARKAR"},
        {"admission_number": "452425", "name": "AYUSH ORAON"},
        {"admission_number": "492526", "name": "AYUSH SINGHA"},
        {"admission_number": "502425", "name": "DEEPSHIKHA ORAON"},
        {"admission_number": "062425", "name": "HABIBA KHATUN"},
        {"admission_number": "442425", "name": "HIMANSHU SARKAR"},
        {"admission_number": "152425", "name": "ISHANI SARKAR"},
        {"admission_number": "212324", "name": "MAHAMMAD REHAN"},
        {"admission_number": "132425", "name": "MANASVI KUMARI"},
        {"admission_number": "072425", "name": "NOYNIKA PAUL"},
        {"admission_number": "252425", "name": "PIU SHAW"},
        {"admission_number": "222425", "name": "PRIYAM PAUL"},
        {"admission_number": "362425", "name": "RIDHIMA DAS"},
        {"admission_number": "392425", "name": "RUDRANEEL GHOSH"},
        {"admission_number": "192425", "name": "RUPAM BARMAN"},
        {"admission_number": "172324", "name": "SANDIP TOPPO"},
        {"admission_number": "412425", "name": "SHOVAN SARKAR"}
      ],
      "Class 2": [
        {"admission_number": "472223", "name": "ABHI SHA"},
        {"admission_number": "292526", "name": "ADITYA JHA"},
        {"admission_number": "92526", "name": "ADITYA ROY"},
        {"admission_number": "1003", "name": "AHIL JAMAN"},
        {"admission_number": "1004", "name": "AISHIKI DEB DAS"},
        {"admission_number": "332324", "name": "ANJALI ORAON"},
        {"admission_number": "362324", "name": "ANKAN BARMAN"},
        {"admission_number": "482324", "name": "ANWESHA SARKAR"},
        {"admission_number": "372324", "name": "APURBA DEY"},
        {"admission_number": "1005", "name": "AROHI SARKAR"},
        {"admission_number": "262324", "name": "BARSHA RAY"},
        {"admission_number": "1006", "name": "BIKASH ORAON"},
        {"admission_number": "1007", "name": "DIPANWITA ROY"},
        {"admission_number": "1008", "name": "ELINA MUNDA"},
        {"admission_number": "302324", "name": "KABIR ORAON"},
        {"admission_number": "512324", "name": "KRISHNA SARKAR"},
        {"admission_number": "1009", "name": "MANJORI DAS"},
        {"admission_number": "1010", "name": "MAUSAM TOPPO"},
        {"admission_number": "1011", "name": "MUSKAN ROY"},
        {"admission_number": "082324", "name": "NANDINI SHA"},
        {"admission_number": "382324", "name": "NEEL KAMAL BARMAN"},
        {"admission_number": "492324", "name": "PRIYAM BARMAN"},
        {"admission_number": "182324", "name": "RAHUL ORAON"},
        {"admission_number": "1012", "name": "RITTIKA SARKAR"},
        {"admission_number": "462324", "name": "ROHIT MINZ"},
        {"admission_number": "272324", "name": "SHILPI ORAON"},
        {"admission_number": "062324", "name": "SHUBHAM SARKAR"},
        {"admission_number": "322324", "name": "SNEHA SARKAR"},
        {"admission_number": "022324", "name": "SOHAM SARKAR"},
        {"admission_number": "042324", "name": "SOUMYADIP DAS"},
        {"admission_number": "1013", "name": "SOURAV SARKAR"},
        {"admission_number": "502324", "name": "SRESTHA DUTTA"},
        {"admission_number": "352324", "name": "SUSMITA ORAON"},
        {"admission_number": "242324", "name": "TANISHKA ROY"}
      ],
      "Class 3": [
        {"admission_number": "1014", "name": "ADITYA SAH"},
        {"admission_number": "252526", "name": "AGNIV MAITY"},
        {"admission_number": "232223", "name": "ALVINA AHMED"},
        {"admission_number": "202223", "name": "ANANYA SHA"},
        {"admission_number": "332526", "name": "ANIK HALDAR"},
        {"admission_number": "1015", "name": "ANISH SARKAR"},
        {"admission_number": "412223", "name": "ANUSREE DAS"},
        {"admission_number": "1016", "name": "ARITRA DEB"},
        {"admission_number": "1017", "name": "ARPITA BARMAN"},
        {"admission_number": "1018", "name": "BAISHALI MODAK"},
        {"admission_number": "532223", "name": "BISHAL MUNDA"},
        {"admission_number": "1019", "name": "BISHAL RABHA"},
        {"admission_number": "1020", "name": "DHRITISUNDAR SEN"},
        {"admission_number": "032223", "name": "DIPIKA SHAW"},
        {"admission_number": "272223", "name": "HIYA PAUL"},
        {"admission_number": "422223", "name": "ISHAN SARKAR"},
        {"admission_number": "072223", "name": "JASMINE SARKAR"},
        {"admission_number": "1021", "name": "MADHUMITA MAHATO"},
        {"admission_number": "312223", "name": "MONALISHA SUBBA"},
        {"admission_number": "222223", "name": "NAMRATA PAUL"},
        {"admission_number": "1022", "name": "PUJA KUJUR"},
        {"admission_number": "432223", "name": "RAJNANDINI SARKAR"},
        {"admission_number": "162223", "name": "RANI ORAON"},
        {"admission_number": "282223", "name": "RISHI PAUL"},
        {"admission_number": "012223", "name": "RIYA SARKAR"},
        {"admission_number": "1023", "name": "RUPKATHA ROY"},
        {"admission_number": "352223", "name": "SAKSHI LAKRA"},
        {"admission_number": "052223", "name": "SAMRIDDHI ROY"},
        {"admission_number": "452223", "name": "SAYAN SARKAR"},
        {"admission_number": "382223", "name": "SHRADDHA ORAON"},
        {"admission_number": "1024", "name": "SHREYA BISWAS"},
        {"admission_number": "402223", "name": "SNEHA SAHA"},
        {"admission_number": "512223", "name": "SOHAM KARMAKAR"},
        {"admission_number": "242223", "name": "SUBHANKAR BARMAN"},
        {"admission_number": "112223", "name": "SUDIPTA SARKAR"},
        {"admission_number": "302223", "name": "SUMAN SARKAR"},
        {"admission_number": "132223", "name": "SWARUP SAHA"}
      ],
      "Class 4": [
        {"admission_number": "792425", "name": "AARAV UPADHYAY"},
        {"admission_number": "61920", "name": "ADITYA GHOSH"},
        {"admission_number": "1025", "name": "ADITYA KAMI"},
        {"admission_number": "371920", "name": "ANIK ROY"},
        {"admission_number": "711819", "name": "ANIKA ORAON"},
        {"admission_number": "1026", "name": "ANISHA SHAW"},
        {"admission_number": "491920", "name": "ARJUN SARKAR"},
        {"admission_number": "42122", "name": "BIRAJ BARMAN"},
        {"admission_number": "592122", "name": "DIYA DAS"},
        {"admission_number": "52122", "name": "HENA BARMAN"},
        {"admission_number": "82122", "name": "HIMANSHU MANDAL"},
        {"admission_number": "1027", "name": "JASMINE PUKHRAMBAM"},
        {"admission_number": "62122", "name": "JEET BARMAN"},
        {"admission_number": "482122", "name": "JOY DAS"},
        {"admission_number": "1028", "name": "KABIR SARKAR"},
        {"admission_number": "632122", "name": "MANISH KERKETTA"},
        {"admission_number": "1029", "name": "MD SAIF"},
        {"admission_number": "512122", "name": "MITALI TOPPO"},
        {"admission_number": "522122", "name": "MUSKAN ORAON"},
        {"admission_number": "132122", "name": "NANDITA ORAON"},
        {"admission_number": "1030", "name": "PRAGYA GUPTA"},
        {"admission_number": "172122", "name": "PRIYANGSHU KARMAKAR"},
        {"admission_number": "152122", "name": "RABINA MINZ"},
        {"admission_number": "1031", "name": "RAJDEEP DAS"},
        {"admission_number": "542122", "name": "RIYA KUJUR"},
        {"admission_number": "1032", "name": "RIYA ROY"},
        {"admission_number": "212122", "name": "ROHIT MUNDA"},
        {"admission_number": "472122", "name": "SAMRAT ORAON"},
        {"admission_number": "1033", "name": "SHREYA PAUL"},
        {"admission_number": "252122", "name": "SIMRAN TIGGA"},
        {"admission_number": "602122", "name": "SNEHA KERKETTA"},
        {"admission_number": "422122", "name": "SOHINI DUTTA"},
        {"admission_number": "572122", "name": "SOURAV SARKAR"},
        {"admission_number": "642122", "name": "SUBHAJIT BARMAN"},
        {"admission_number": "402122", "name": "SUBHAM LAKRA"},
        {"admission_number": "432122", "name": "SWEETY ORAON"}
      ],
      "Class 5": [
        {"admission_number": "442021", "name": "ABHINAV SINGH"},
        {"admission_number": "1034", "name": "ABIN LAKRA"},
        {"admission_number": "892425", "name": "ANIK GOSWAMI"},
        {"admission_number": "1035", "name": "ANIKET AGARWAL"},
        {"admission_number": "512223", "name": "ANKIT KARMAKAR"},
        {"admission_number": "462021", "name": "ANUSKA MURMU"},
        {"admission_number": "142021", "name": "ARPAN HALDAR"},
        {"admission_number": "32021", "name": "ARYAN MANDAL"},
        {"admission_number": "252021", "name": "DONA SARKAR"},
        {"admission_number": "862425", "name": "KABITA KUJUR"},
        {"admission_number": "42021", "name": "MAYURI BARMAN"},
        {"admission_number": "212021", "name": "MD ZAID"},
        {"admission_number": "842425", "name": "NEHA ORAON"},
        {"admission_number": "1036", "name": "NITU EKKA"},
        {"admission_number": "72021", "name": "PALASH SARKAR"},
        {"admission_number": "1037", "name": "PRASENJIT SARKAR"},
        {"admission_number": "392021", "name": "PRIYANKA MINZ"},
        {"admission_number": "222021", "name": "RAUNAK RAJ"},
        {"admission_number": "232021", "name": "RISHAV SARKAR"},
        {"admission_number": "1038", "name": "SHARMISTHA PAUL"},
        {"admission_number": "472021", "name": "SNEHA EKKA"},
        {"admission_number": "262021", "name": "SOHINI ORAON"},
        {"admission_number": "102021", "name": "SUDIPTA DAS"},
        {"admission_number": "352021", "name": "TANMAY BISWAS"}
      ],
      "Class 6": [
        {"admission_number": "312324", "name": "AARADHYA RAY"},
        {"admission_number": "1039", "name": "ADITI MAJUMDAR"},
        {"admission_number": "1040", "name": "ARNAB ROY"},
        {"admission_number": "1041", "name": "DEV ROY"},
        {"admission_number": "151920", "name": "DILSHAN HOSSAIN"},
        {"admission_number": "181920", "name": "KARISHMA LAKRA"},
        {"admission_number": "1042", "name": "NAVNEET ORAON"},
        {"admission_number": "1043", "name": "RACHANA MUNDA"},
        {"admission_number": "661920", "name": "RITESH ORAON"},
        {"admission_number": "891920", "name": "ROHIT LAKRA"},
        {"admission_number": "191920", "name": "SATYAM RAJ"},
        {"admission_number": "651920", "name": "SONU KUJUR"},
        {"admission_number": "1044", "name": "SUPRIYA MINZ"}
      ],
      "Class 7": [
        {"admission_number": "1045", "name": "AGNIK DAS"},
        {"admission_number": "1046", "name": "ANIK SAHA"},
        {"admission_number": "101920", "name": "ANIMESH TOPPO"},
        {"admission_number": "1047", "name": "ANNESHA BARMAN"},
        {"admission_number": "231920", "name": "APURBA DATTA"},
        {"admission_number": "201920", "name": "ARPAN GOPE"},
        {"admission_number": "451920", "name": "BHUMIKA ORAON"},
        {"admission_number": "261920", "name": "DEBASMITA SARKAR"},
        {"admission_number": "941920", "name": "HIMANGSHU KARMAKAR"},
        {"admission_number": "461920", "name": "KABITA ORAON"},
        {"admission_number": "301920", "name": "KOUSHIK SARKAR"},
        {"admission_number": "1048", "name": "KUSHAL SHA"},
        {"admission_number": "1049", "name": "MADHUMITA SARKAR"},
        {"admission_number": "431920", "name": "MAHIMA BARAIK"},
        {"admission_number": "1050", "name": "MILI SOREN"},
        {"admission_number": "1051", "name": "MIRA SARDAR"},
        {"admission_number": "1052", "name": "PAPIYA ROY"},
        {"admission_number": "401920", "name": "PRASENJIT ORAON"},
        {"admission_number": "281920", "name": "PRITI MUNDA"},
        {"admission_number": "1053", "name": "PUJA BARMAN"},
        {"admission_number": "391920", "name": "RABI ORAON"},
        {"admission_number": "441920", "name": "RANI ORAON"},
        {"admission_number": "1054", "name": "RESHMI TIGGA"},
        {"admission_number": "701920", "name": "RIYA BARMAN"},
        {"admission_number": "1055", "name": "SAYAN SARKAR"},
        {"admission_number": "1056", "name": "SHILPI TOPPO"},
        {"admission_number": "921920", "name": "SHUBHAM ORAON"},
        {"admission_number": "241920", "name": "SNEHA SARKAR"},
        {"admission_number": "341920", "name": "SOURAV SARKAR"},
        {"admission_number": "501920", "name": "SUMAN TIRKEY"}
      ],
      "Class 8": [
        {"admission_number": "1062", "name": "ADARS KAMI"},
        {"admission_number": "1063", "name": "AGNITRA DUTTA"},
        {"admission_number": "942425", "name": "ANNESHA GOPE"},
        {"admission_number": "71920", "name": "APU KARMAKAR"},
        {"admission_number": "1064", "name": "ARADHYA SARKAR"},
        {"admission_number": "1065", "name": "ARFIN ALAM"},
        {"admission_number": "1066", "name": "ARITRI NANDI"},
        {"admission_number": "1067", "name": "ARUP KUJUR"},
        {"admission_number": "1068", "name": "AYUSH KUMAR SINGH"},
        {"admission_number": "1069", "name": "DEEPSEKHAR ROY"},
        {"admission_number": "512526", "name": "DHRUB BISWAS"},
        {"admission_number": "522526", "name": "DILSUN ORAON"},
        {"admission_number": "142324", "name": "GURU MAHATO"},
        {"admission_number": "1070", "name": "ISMRITI SHARMA"},
        {"admission_number": "102324", "name": "KHUSHI MAHATO"},
        {"admission_number": "642324", "name": "KOYAL ROY"},
        {"admission_number": "1071", "name": "LOKESH ROY"},
        {"admission_number": "1072", "name": "MD RIZWAN KHAN"},
        {"admission_number": "602526", "name": "MUSKAAN SARKAR"},
        {"admission_number": "401617", "name": "RANAP BARMAN"},
        {"admission_number": "1073", "name": "RINOF ARMAN"},
        {"admission_number": "1074", "name": "RIYA SHA"},
        {"admission_number": "1075", "name": "TRISHITA BASAK"}
      ],
      "Class 9": [
        {"admission_number": "1076", "name": "ALEX KUJUR"},
        {"admission_number": "592526", "name": "ANIRODH ORAON"},
        {"admission_number": "1077", "name": "ANIRUDHA SARKAR"},
        {"admission_number": "1078", "name": "ARGHADEEP SAHA"},
        {"admission_number": "601920", "name": "ARNAB KUNDU"},
        {"admission_number": "642425", "name": "CHATAN MONDAL"},
        {"admission_number": "1079", "name": "CHIRASMITA SARKAR"},
        {"admission_number": "1080", "name": "DEEPAK SHARMA"},
        {"admission_number": "1081", "name": "DIVYAJYATI BISWAS"},
        {"admission_number": "1082", "name": "DOLI SHARMA"},
        {"admission_number": "1083", "name": "EMRAN ALI"},
        {"admission_number": "1084", "name": "MAMUN MIYA"},
        {"admission_number": "1085", "name": "NILABHRA DHAR"},
        {"admission_number": "1086", "name": "PRITAM SAH"},
        {"admission_number": "522425", "name": "PUNAM XALXO"},
        {"admission_number": "1087", "name": "RAHUL ROY"},
        {"admission_number": "592223", "name": "RAJ BASFORE"},
        {"admission_number": "862324", "name": "RUDRANIL DATTA"},
        {"admission_number": "1088", "name": "VIYA ROY KARMAKAR"}
      ],
      "Class 10": [
        {"admission_number": "2089", "name": "AAFRIN ANSARI"},
        {"admission_number": "672425", "name": "ALISHA ANSARI"},
        {"admission_number": "351415", "name": "ANAMIKA BARMAN"},
        {"admission_number": "1090", "name": "ANKITA HORE"},
        {"admission_number": "1091", "name": "ARINDAM MINZ"},
        {"admission_number": "1092", "name": "AYUSH SARKAR"},
        {"admission_number": "1093", "name": "BIRAJ MINJ"},
        {"admission_number": "1094", "name": "BUBAI ORAON"},
        {"admission_number": "862425", "name": "DHRITIPARNA BALL"},
        {"admission_number": "652324", "name": "JULI ORAON"},
        {"admission_number": "912425", "name": "NOBONITA DAS"},
        {"admission_number": "1095", "name": "RANADWEEP GHOSH"},
        {"admission_number": "1096", "name": "RIYAN ROY"},
        {"admission_number": "632223", "name": "ROHAN THAKUR"},
        {"admission_number": "1097", "name": "SANIA HUSSAIN"},
        {"admission_number": "472425", "name": "SREYOSHI GHOSH"},
        {"admission_number": "1098", "name": "SURASREE SARKAR"}
      ],
      "LKG": [
        {"admission_number": "312526", "name": "AARUSHI ADHIKARI"},
        {"admission_number": "322425", "name": "ABHAY SEN"},
        {"admission_number": "222425", "name": "AD BROCK  TOPNO"},
        {"admission_number": "252425", "name": "ADRIJA SARKAR"},
        {"admission_number": "182526", "name": "ANIKET BARMAN"},
        {"admission_number": "492526", "name": "ARNA DAS"},
        {"admission_number": "302425", "name": "AROHI ROY"},
        {"admission_number": "1225260", "name": "AYAAN RAHAMAN"},
        {"admission_number": "562425", "name": "AYUSH BARMAN"},
        {"admission_number": "122425", "name": "BANSHIKA LOHAR"},
        {"admission_number": "452425", "name": "BARBI BARMAN"},
        {"admission_number": "372526", "name": "BIRAJ BARMAN"},
        {"admission_number": "132526", "name": "BRIJRAJ BARMAN"},
        {"admission_number": "392425", "name": "CHIRADEEP ORAON"},
        {"admission_number": "152526", "name": "DEBAPRIYA BARMAN"},
        {"admission_number": "382526", "name": "DIPANKAR JANA"},
        {"admission_number": "462526", "name": "DRISHANT ROY"},
        {"admission_number": "82425", "name": "GOURAB PAUL"},
        {"admission_number": "192526", "name": "ISAN HOSSAIN"},
        {"admission_number": "22526", "name": "ISHIKA ROY"},
        {"admission_number": "222526", "name": "KAYRA KUMARI GIRI"},
        {"admission_number": "162526", "name": "KUNDAN ROY"},
        {"admission_number": "62425", "name": "NASIFA MALIK"},
        {"admission_number": "202425", "name": "PARTHIV SARKAR"},
        {"admission_number": "682425", "name": "PEHAL AGARWAL"},
        {"admission_number": "332425", "name": "PRIANSHI KANTHA"},
        {"admission_number": "362425", "name": "PRIYANSHU SHA"},
        {"admission_number": "42425", "name": "RAHAT HAQUE"},
        {"admission_number": "372425", "name": "RIJBHAN ALAM"},
        {"admission_number": "72526", "name": "RIYANSHI DAS"},
        {"admission_number": "212425", "name": "RUHI ORAON"},
        {"admission_number": "352425", "name": "SAMRIDDHI ROY"},
        {"admission_number": "482425", "name": "SARTHAK DAS"},
        {"admission_number": "272425", "name": "SARTHAK GHOSH DASTIDAR"},
        {"admission_number": "262425", "name": "SOURAV PAUL"},
        {"admission_number": "132425", "name": "SOYTA BARWAR"},
        {"admission_number": "562526", "name": "SUBHOJIT TALUKDAR"},
        {"admission_number": "22425", "name": "YASH ADHIKARI"}
      ],
      "Nursery": [
        {"admission_number": "400", "name": "AISSA ABDA"},
        {"admission_number": "501", "name": "MAHMOUD OUBERI"},
        {"admission_number": "502", "name": "HIBA OUBER"},
        {"admission_number": "503", "name": "CHAYMAE QASMI"},
        {"admission_number": "504", "name": "WALID  ABD"},
        {"admission_number": "82526", "name": "AADIYA ROY"},
        {"admission_number": "272526", "name": "AMRITA ROY"},
        {"admission_number": "282526", "name": "AYUSH MANDAL"},
        {"admission_number": "322526", "name": "BIBHAN CHAKRABORTY"},
        {"admission_number": "362526", "name": "DISHITA BARMAN"},
        {"admission_number": "52526", "name": "DRITI BARMAN"},
        {"admission_number": "572526", "name": "DURJOY DAS"},
        {"admission_number": "342526", "name": "HIYAN PAUL"},
        {"admission_number": "122526", "name": "ISHIKA DAS"},
        {"admission_number": "452526", "name": "JIYANSI BIR"},
        {"admission_number": "42526", "name": "MANDIRA SAHA"},
        {"admission_number": "242526", "name": "NIKHIL SAH"},
        {"admission_number": "232526", "name": "OISHI PAUL"},
        {"admission_number": "112526", "name": "PRIHAN ROY"},
        {"admission_number": "142526", "name": "RIZWAN ISLAM"},
        {"admission_number": "202526", "name": "RONITA CHANDRAMA"},
        {"admission_number": "352526", "name": "RUSHAN PAUL"},
        {"admission_number": "502526", "name": "SANVIKA BARMAN"},
        {"admission_number": "302526", "name": "SAYAN SARKAR"},
        {"admission_number": "62526", "name": "SIMON HANSDA"},
        {"admission_number": "442526", "name": "SRIYANSH LASKAR"},
        {"admission_number": "172526", "name": "TRINANGSHI DUTTA"}
      ]
    };
    
    if (!window.confirm(`এই Excel data থেকে students restore করতে চান?\n\nTotal: ${Object.values(classStudentMapping).flat().length} students\n\nএটা করলে:\n- সব students সঠিক class এ যাবে\n- সব 2026-2027 invoices delete হবে\n- সব promotion history delete হবে`)) {
      return;
    }
    
    setRestoringFromExcel(true);
    setRestoreResult(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/restore-classes-from-data`,
        classStudentMapping,
        { headers: getAuthHeader() }
      );
      setRestoreResult(res.data);
      toast.success(res.data.message);
      fetchClassSummary();
      fetchDataSummary(selectedSchool);
    } catch (error) {
      toast.error('Restore failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRestoringFromExcel(false);
    }
  };

  // DELETE ALL STUDENTS - Using Background Job
  const deleteAllStudents = async () => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    
    if (!window.confirm('⚠️ WARNING! এটা সব STUDENTS DELETE করে দেবে!\n\nসাথে delete হবে:\n- সব Invoices\n- সব Payments\n- সব Fee Assignments\n- সব Promotion History\n\nআপনি কি 100% নিশ্চিত?')) {
      return;
    }
    
    if (!window.confirm('🔴 FINAL WARNING!\n\nএই action UNDO করা যাবে না!\n\nসত্যিই সব students delete করতে চান?')) {
      return;
    }
    
    setDeletingAllStudents(true);
    try {
      // Try background job first
      await startBackgroundJob(
        `/api/jobs/delete-all-students/${selectedSchool}`,
        null,
        'delete_students',
        (result) => {
          setDeletingAllStudents(false);
          setImportResult(result);
        }
      );
    } catch (error) {
      // Fallback to direct API call
      try {
        const res = await axios.delete(
          `${API_URL}/api/super-admin/tenant/${selectedSchool}/delete-all-students?confirm=true`,
          { headers: getAuthHeader() }
        );
        toast.success(res.data.message);
        fetchDataSummary(selectedSchool);
        fetchClassSummary();
        setDeletingAllStudents(false);
      } catch (err) {
        toast.error('Delete failed: ' + (err.response?.data?.detail || err.message));
        setDeletingAllStudents(false);
      }
    }
  };

  // IMPORT STUDENTS FROM EXCEL
  const importStudentsFromExcel = async () => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    
    // Same classStudentMapping as restoreFromExcelData
    const classStudentMapping = {
      "UKG": [
        {"admission_number": "142425", "name": "AAROHI SAHA"},
        {"admission_number": "472526", "name": "AAYAN KAMI"},
        {"admission_number": "422526", "name": "ADITI MUNDA"},
        {"admission_number": "092425", "name": "ADRISHA BHOWMIK"},
        {"admission_number": "232324", "name": "ADWITIYA DAS"},
        {"admission_number": "312425", "name": "AHONA SARKAR"},
        {"admission_number": "412324", "name": "AKASH ROY"},
        {"admission_number": "112425", "name": "ANISHA DAS"},
        {"admission_number": "512526", "name": "ANWESHA TOPPO"},
        {"admission_number": "392324", "name": "ARINJOY SARKAR"},
        {"admission_number": "092526", "name": "ARITRA BARMAN"},
        {"admission_number": "502526", "name": "ARMAN SK"},
        {"admission_number": "372526", "name": "AROHI RAY"},
        {"admission_number": "102425", "name": "AYUSH BARMAN"},
        {"admission_number": "152526", "name": "BIDIPTA SARKAR"},
        {"admission_number": "262526", "name": "DEBJANI SAHA"},
        {"admission_number": "332526", "name": "DISHA BARAI"},
        {"admission_number": "352526", "name": "ISHIKA LAKRA"},
        {"admission_number": "222526", "name": "JINIA SARKAR"},
        {"admission_number": "052526", "name": "JISAN SK"},
        {"admission_number": "532526", "name": "JOYEETA CHOUDHURY"},
        {"admission_number": "012526", "name": "KAVERI LOHAR"},
        {"admission_number": "402526", "name": "KINJAL SARKAR"},
        {"admission_number": "132526", "name": "LIYANSH GHOSH"},
        {"admission_number": "302526", "name": "MAYUKH DEY"},
        {"admission_number": "442526", "name": "MEGHNA SHA"},
        {"admission_number": "212425", "name": "MOU SARKAR"},
        {"admission_number": "362526", "name": "NOYONIKA ROY"},
        {"admission_number": "242526", "name": "PRATYUSH GHOSH"},
        {"admission_number": "122526", "name": "PRIYANSHU SARKAR"},
        {"admission_number": "452526", "name": "REYAANSH ORAON"},
        {"admission_number": "192526", "name": "RIYANSH RAY"},
        {"admission_number": "062526", "name": "RUDRA SARKAR"},
        {"admission_number": "162526", "name": "RUPSHA SARKAR"},
        {"admission_number": "022526", "name": "RYAN BARMAN"},
        {"admission_number": "272526", "name": "SAYAN BARMAN"},
        {"admission_number": "172526", "name": "SHIVAM SARKAR"},
        {"admission_number": "382526", "name": "SHREYA GUPTA"},
        {"admission_number": "202526", "name": "SOUMYAJIT DAS"},
        {"admission_number": "282526", "name": "TANISHA KHARIA"},
        {"admission_number": "072526", "name": "UTSAB BARMAN"}
      ],
      "Class 1": [
        {"admission_number": "422324", "name": "AAHAN DAS"},
        {"admission_number": "1000", "name": "ABU BAKKAR SIDDIK"},
        {"admission_number": "432526", "name": "ADRIT MODAK"},
        {"admission_number": "112324", "name": "AGNIV ROY"},
        {"admission_number": "1001", "name": "AMBIKA ROY"},
        {"admission_number": "462526", "name": "ANGEL MUNDA"},
        {"admission_number": "342425", "name": "ANUSHKA ROY"},
        {"admission_number": "032425", "name": "ARCHI SAHA"},
        {"admission_number": "352425", "name": "ARIBA KHATUN"},
        {"admission_number": "242425", "name": "ARIJIT MANDAL"},
        {"admission_number": "322425", "name": "AROHI DAS"},
        {"admission_number": "1002", "name": "ARSHAN MD"},
        {"admission_number": "042425", "name": "ARYA SARKAR"},
        {"admission_number": "452425", "name": "AYUSH ORAON"},
        {"admission_number": "492526", "name": "AYUSH SINGHA"},
        {"admission_number": "502425", "name": "DEEPSHIKHA ORAON"},
        {"admission_number": "062425", "name": "HABIBA KHATUN"},
        {"admission_number": "442425", "name": "HIMANSHU SARKAR"},
        {"admission_number": "152425", "name": "ISHANI SARKAR"},
        {"admission_number": "212324", "name": "MAHAMMAD REHAN"},
        {"admission_number": "132425", "name": "MANASVI KUMARI"},
        {"admission_number": "072425", "name": "NOYNIKA PAUL"},
        {"admission_number": "252425", "name": "PIU SHAW"},
        {"admission_number": "222425", "name": "PRIYAM PAUL"},
        {"admission_number": "362425", "name": "RIDHIMA DAS"},
        {"admission_number": "392425", "name": "RUDRANEEL GHOSH"},
        {"admission_number": "192425", "name": "RUPAM BARMAN"},
        {"admission_number": "172324", "name": "SANDIP TOPPO"},
        {"admission_number": "412425", "name": "SHOVAN SARKAR"}
      ],
      "Class 2": [
        {"admission_number": "472223", "name": "ABHI SHA"},
        {"admission_number": "292526", "name": "ADITYA JHA"},
        {"admission_number": "92526", "name": "ADITYA ROY"},
        {"admission_number": "1003", "name": "AHIL JAMAN"},
        {"admission_number": "1004", "name": "AISHIKI DEB DAS"},
        {"admission_number": "332324", "name": "ANJALI ORAON"},
        {"admission_number": "362324", "name": "ANKAN BARMAN"},
        {"admission_number": "482324", "name": "ANWESHA SARKAR"},
        {"admission_number": "372324", "name": "APURBA DEY"},
        {"admission_number": "1005", "name": "AROHI SARKAR"},
        {"admission_number": "262324", "name": "BARSHA RAY"},
        {"admission_number": "1006", "name": "BIKASH ORAON"},
        {"admission_number": "1007", "name": "DIPANWITA ROY"},
        {"admission_number": "1008", "name": "ELINA MUNDA"},
        {"admission_number": "302324", "name": "KABIR ORAON"},
        {"admission_number": "512324", "name": "KRISHNA SARKAR"},
        {"admission_number": "1009", "name": "MANJORI DAS"},
        {"admission_number": "1010", "name": "MAUSAM TOPPO"},
        {"admission_number": "1011", "name": "MUSKAN ROY"},
        {"admission_number": "082324", "name": "NANDINI SHA"},
        {"admission_number": "382324", "name": "NEEL KAMAL BARMAN"},
        {"admission_number": "492324", "name": "PRIYAM BARMAN"},
        {"admission_number": "182324", "name": "RAHUL ORAON"},
        {"admission_number": "1012", "name": "RITTIKA SARKAR"},
        {"admission_number": "462324", "name": "ROHIT MINZ"},
        {"admission_number": "272324", "name": "SHILPI ORAON"},
        {"admission_number": "062324", "name": "SHUBHAM SARKAR"},
        {"admission_number": "322324", "name": "SNEHA SARKAR"},
        {"admission_number": "022324", "name": "SOHAM SARKAR"},
        {"admission_number": "042324", "name": "SOUMYADIP DAS"},
        {"admission_number": "1013", "name": "SOURAV SARKAR"},
        {"admission_number": "502324", "name": "SRESTHA DUTTA"},
        {"admission_number": "352324", "name": "SUSMITA ORAON"},
        {"admission_number": "242324", "name": "TANISHKA ROY"}
      ],
      "Class 3": [
        {"admission_number": "1014", "name": "ADITYA SAH"},
        {"admission_number": "252526", "name": "AGNIV MAITY"},
        {"admission_number": "232223", "name": "ALVINA AHMED"},
        {"admission_number": "202223", "name": "ANANYA SHA"},
        {"admission_number": "332526", "name": "ANIK HALDAR"},
        {"admission_number": "1015", "name": "ANISH SARKAR"},
        {"admission_number": "412223", "name": "ANUSREE DAS"},
        {"admission_number": "1016", "name": "ARITRA DEB"},
        {"admission_number": "1017", "name": "ARPITA BARMAN"},
        {"admission_number": "1018", "name": "BAISHALI MODAK"},
        {"admission_number": "532223", "name": "BISHAL MUNDA"},
        {"admission_number": "1019", "name": "BISHAL RABHA"},
        {"admission_number": "1020", "name": "DHRITISUNDAR SEN"},
        {"admission_number": "032223", "name": "DIPIKA SHAW"},
        {"admission_number": "272223", "name": "HIYA PAUL"},
        {"admission_number": "422223", "name": "ISHAN SARKAR"},
        {"admission_number": "072223", "name": "JASMINE SARKAR"},
        {"admission_number": "1021", "name": "MADHUMITA MAHATO"},
        {"admission_number": "312223", "name": "MONALISHA SUBBA"},
        {"admission_number": "222223", "name": "NAMRATA PAUL"},
        {"admission_number": "1022", "name": "PUJA KUJUR"},
        {"admission_number": "432223", "name": "RAJNANDINI SARKAR"},
        {"admission_number": "162223", "name": "RANI ORAON"},
        {"admission_number": "282223", "name": "RISHI PAUL"},
        {"admission_number": "012223", "name": "RIYA SARKAR"},
        {"admission_number": "1023", "name": "RUPKATHA ROY"},
        {"admission_number": "352223", "name": "SAKSHI LAKRA"},
        {"admission_number": "052223", "name": "SAMRIDDHI ROY"},
        {"admission_number": "452223", "name": "SAYAN SARKAR"},
        {"admission_number": "382223", "name": "SHRADDHA ORAON"},
        {"admission_number": "1024", "name": "SHREYA BISWAS"},
        {"admission_number": "402223", "name": "SNEHA SAHA"},
        {"admission_number": "512223", "name": "SOHAM KARMAKAR"},
        {"admission_number": "242223", "name": "SUBHANKAR BARMAN"},
        {"admission_number": "112223", "name": "SUDIPTA SARKAR"},
        {"admission_number": "302223", "name": "SUMAN SARKAR"},
        {"admission_number": "132223", "name": "SWARUP SAHA"}
      ],
      "Class 4": [
        {"admission_number": "792425", "name": "AARAV UPADHYAY"},
        {"admission_number": "61920", "name": "ADITYA GHOSH"},
        {"admission_number": "1025", "name": "ADITYA KAMI"},
        {"admission_number": "371920", "name": "ANIK ROY"},
        {"admission_number": "711819", "name": "ANIKA ORAON"},
        {"admission_number": "1026", "name": "ANISHA SHAW"},
        {"admission_number": "491920", "name": "ARJUN SARKAR"},
        {"admission_number": "42122", "name": "BIRAJ BARMAN"},
        {"admission_number": "592122", "name": "DIYA DAS"},
        {"admission_number": "52122", "name": "HENA BARMAN"},
        {"admission_number": "82122", "name": "HIMANSHU MANDAL"},
        {"admission_number": "1027", "name": "JASMINE PUKHRAMBAM"},
        {"admission_number": "62122", "name": "JEET BARMAN"},
        {"admission_number": "482122", "name": "JOY DAS"},
        {"admission_number": "1028", "name": "KABIR SARKAR"},
        {"admission_number": "632122", "name": "MANISH KERKETTA"},
        {"admission_number": "1029", "name": "MD SAIF"},
        {"admission_number": "512122", "name": "MITALI TOPPO"},
        {"admission_number": "522122", "name": "MUSKAN ORAON"},
        {"admission_number": "132122", "name": "NANDITA ORAON"},
        {"admission_number": "1030", "name": "PRAGYA GUPTA"},
        {"admission_number": "172122", "name": "PRIYANGSHU KARMAKAR"},
        {"admission_number": "152122", "name": "RABINA MINZ"},
        {"admission_number": "1031", "name": "RAJDEEP DAS"},
        {"admission_number": "542122", "name": "RIYA KUJUR"},
        {"admission_number": "1032", "name": "RIYA ROY"},
        {"admission_number": "212122", "name": "ROHIT MUNDA"},
        {"admission_number": "472122", "name": "SAMRAT ORAON"},
        {"admission_number": "1033", "name": "SHREYA PAUL"},
        {"admission_number": "252122", "name": "SIMRAN TIGGA"},
        {"admission_number": "602122", "name": "SNEHA KERKETTA"},
        {"admission_number": "422122", "name": "SOHINI DUTTA"},
        {"admission_number": "572122", "name": "SOURAV SARKAR"},
        {"admission_number": "642122", "name": "SUBHAJIT BARMAN"},
        {"admission_number": "402122", "name": "SUBHAM LAKRA"},
        {"admission_number": "432122", "name": "SWEETY ORAON"}
      ],
      "Class 5": [
        {"admission_number": "442021", "name": "ABHINAV SINGH"},
        {"admission_number": "1034", "name": "ABIN LAKRA"},
        {"admission_number": "892425", "name": "ANIK GOSWAMI"},
        {"admission_number": "1035", "name": "ANIKET AGARWAL"},
        {"admission_number": "512223", "name": "ANKIT KARMAKAR"},
        {"admission_number": "462021", "name": "ANUSKA MURMU"},
        {"admission_number": "142021", "name": "ARPAN HALDAR"},
        {"admission_number": "32021", "name": "ARYAN MANDAL"},
        {"admission_number": "252021", "name": "DONA SARKAR"},
        {"admission_number": "862425", "name": "KABITA KUJUR"},
        {"admission_number": "42021", "name": "MAYURI BARMAN"},
        {"admission_number": "212021", "name": "MD ZAID"},
        {"admission_number": "842425", "name": "NEHA ORAON"},
        {"admission_number": "1036", "name": "NITU EKKA"},
        {"admission_number": "72021", "name": "PALASH SARKAR"},
        {"admission_number": "1037", "name": "PRASENJIT SARKAR"},
        {"admission_number": "392021", "name": "PRIYANKA MINZ"},
        {"admission_number": "222021", "name": "RAUNAK RAJ"},
        {"admission_number": "232021", "name": "RISHAV SARKAR"},
        {"admission_number": "1038", "name": "SHARMISTHA PAUL"},
        {"admission_number": "472021", "name": "SNEHA EKKA"},
        {"admission_number": "262021", "name": "SOHINI ORAON"},
        {"admission_number": "102021", "name": "SUDIPTA DAS"},
        {"admission_number": "352021", "name": "TANMAY BISWAS"}
      ],
      "Class 6": [
        {"admission_number": "312324", "name": "AARADHYA RAY"},
        {"admission_number": "1039", "name": "ADITI MAJUMDAR"},
        {"admission_number": "1040", "name": "ARNAB ROY"},
        {"admission_number": "1041", "name": "DEV ROY"},
        {"admission_number": "151920", "name": "DILSHAN HOSSAIN"},
        {"admission_number": "181920", "name": "KARISHMA LAKRA"},
        {"admission_number": "1042", "name": "NAVNEET ORAON"},
        {"admission_number": "1043", "name": "RACHANA MUNDA"},
        {"admission_number": "661920", "name": "RITESH ORAON"},
        {"admission_number": "891920", "name": "ROHIT LAKRA"},
        {"admission_number": "191920", "name": "SATYAM RAJ"},
        {"admission_number": "651920", "name": "SONU KUJUR"},
        {"admission_number": "1044", "name": "SUPRIYA MINZ"}
      ],
      "Class 7": [
        {"admission_number": "1045", "name": "AGNIK DAS"},
        {"admission_number": "1046", "name": "ANIK SAHA"},
        {"admission_number": "101920", "name": "ANIMESH TOPPO"},
        {"admission_number": "1047", "name": "ANNESHA BARMAN"},
        {"admission_number": "231920", "name": "APURBA DATTA"},
        {"admission_number": "201920", "name": "ARPAN GOPE"},
        {"admission_number": "451920", "name": "BHUMIKA ORAON"},
        {"admission_number": "261920", "name": "DEBASMITA SARKAR"},
        {"admission_number": "941920", "name": "HIMANGSHU KARMAKAR"},
        {"admission_number": "461920", "name": "KABITA ORAON"},
        {"admission_number": "301920", "name": "KOUSHIK SARKAR"},
        {"admission_number": "1048", "name": "KUSHAL SHA"},
        {"admission_number": "1049", "name": "MADHUMITA SARKAR"},
        {"admission_number": "431920", "name": "MAHIMA BARAIK"},
        {"admission_number": "1050", "name": "MILI SOREN"},
        {"admission_number": "1051", "name": "MIRA SARDAR"},
        {"admission_number": "1052", "name": "PAPIYA ROY"},
        {"admission_number": "401920", "name": "PRASENJIT ORAON"},
        {"admission_number": "281920", "name": "PRITI MUNDA"},
        {"admission_number": "1053", "name": "PUJA BARMAN"},
        {"admission_number": "391920", "name": "RABI ORAON"},
        {"admission_number": "441920", "name": "RANI ORAON"},
        {"admission_number": "1054", "name": "RESHMI TIGGA"},
        {"admission_number": "701920", "name": "RIYA BARMAN"},
        {"admission_number": "1055", "name": "SAYAN SARKAR"},
        {"admission_number": "1056", "name": "SHILPI TOPPO"},
        {"admission_number": "921920", "name": "SHUBHAM ORAON"},
        {"admission_number": "241920", "name": "SNEHA SARKAR"},
        {"admission_number": "341920", "name": "SOURAV SARKAR"},
        {"admission_number": "501920", "name": "SUMAN TIRKEY"}
      ],
      "Class 8": [
        {"admission_number": "1062", "name": "ADARS KAMI"},
        {"admission_number": "1063", "name": "AGNITRA DUTTA"},
        {"admission_number": "942425", "name": "ANNESHA GOPE"},
        {"admission_number": "71920", "name": "APU KARMAKAR"},
        {"admission_number": "1064", "name": "ARADHYA SARKAR"},
        {"admission_number": "1065", "name": "ARFIN ALAM"},
        {"admission_number": "1066", "name": "ARITRI NANDI"},
        {"admission_number": "1067", "name": "ARUP KUJUR"},
        {"admission_number": "1068", "name": "AYUSH KUMAR SINGH"},
        {"admission_number": "1069", "name": "DEEPSEKHAR ROY"},
        {"admission_number": "512526", "name": "DHRUB BISWAS"},
        {"admission_number": "522526", "name": "DILSUN ORAON"},
        {"admission_number": "142324", "name": "GURU MAHATO"},
        {"admission_number": "1070", "name": "ISMRITI SHARMA"},
        {"admission_number": "102324", "name": "KHUSHI MAHATO"},
        {"admission_number": "642324", "name": "KOYAL ROY"},
        {"admission_number": "1071", "name": "LOKESH ROY"},
        {"admission_number": "1072", "name": "MD RIZWAN KHAN"},
        {"admission_number": "602526", "name": "MUSKAAN SARKAR"},
        {"admission_number": "401617", "name": "RANAP BARMAN"},
        {"admission_number": "1073", "name": "RINOF ARMAN"},
        {"admission_number": "1074", "name": "RIYA SHA"},
        {"admission_number": "1075", "name": "TRISHITA BASAK"}
      ],
      "Class 9": [
        {"admission_number": "1076", "name": "ALEX KUJUR"},
        {"admission_number": "592526", "name": "ANIRODH ORAON"},
        {"admission_number": "1077", "name": "ANIRUDHA SARKAR"},
        {"admission_number": "1078", "name": "ARGHADEEP SAHA"},
        {"admission_number": "601920", "name": "ARNAB KUNDU"},
        {"admission_number": "642425", "name": "CHATAN MONDAL"},
        {"admission_number": "1079", "name": "CHIRASMITA SARKAR"},
        {"admission_number": "1080", "name": "DEEPAK SHARMA"},
        {"admission_number": "1081", "name": "DIVYAJYATI BISWAS"},
        {"admission_number": "1082", "name": "DOLI SHARMA"},
        {"admission_number": "1083", "name": "EMRAN ALI"},
        {"admission_number": "1084", "name": "MAMUN MIYA"},
        {"admission_number": "1085", "name": "NILABHRA DHAR"},
        {"admission_number": "1086", "name": "PRITAM SAH"},
        {"admission_number": "522425", "name": "PUNAM XALXO"},
        {"admission_number": "1087", "name": "RAHUL ROY"},
        {"admission_number": "592223", "name": "RAJ BASFORE"},
        {"admission_number": "862324", "name": "RUDRANIL DATTA"},
        {"admission_number": "1088", "name": "VIYA ROY KARMAKAR"}
      ],
      "Class 10": [
        {"admission_number": "2089", "name": "AAFRIN ANSARI"},
        {"admission_number": "672425", "name": "ALISHA ANSARI"},
        {"admission_number": "351415", "name": "ANAMIKA BARMAN"},
        {"admission_number": "1090", "name": "ANKITA HORE"},
        {"admission_number": "1091", "name": "ARINDAM MINZ"},
        {"admission_number": "1092", "name": "AYUSH SARKAR"},
        {"admission_number": "1093", "name": "BIRAJ MINJ"},
        {"admission_number": "1094", "name": "BUBAI ORAON"},
        {"admission_number": "862425", "name": "DHRITIPARNA BALL"},
        {"admission_number": "652324", "name": "JULI ORAON"},
        {"admission_number": "912425", "name": "NOBONITA DAS"},
        {"admission_number": "1095", "name": "RANADWEEP GHOSH"},
        {"admission_number": "1096", "name": "RIYAN ROY"},
        {"admission_number": "632223", "name": "ROHAN THAKUR"},
        {"admission_number": "1097", "name": "SANIA HUSSAIN"},
        {"admission_number": "472425", "name": "SREYOSHI GHOSH"},
        {"admission_number": "1098", "name": "SURASREE SARKAR"}
      ],
      "LKG": [
        {"admission_number": "312526", "name": "AARUSHI ADHIKARI"},
        {"admission_number": "322425", "name": "ABHAY SEN"},
        {"admission_number": "222425", "name": "AD BROCK  TOPNO"},
        {"admission_number": "252425", "name": "ADRIJA SARKAR"},
        {"admission_number": "182526", "name": "ANIKET BARMAN"},
        {"admission_number": "492526", "name": "ARNA DAS"},
        {"admission_number": "302425", "name": "AROHI ROY"},
        {"admission_number": "1225260", "name": "AYAAN RAHAMAN"},
        {"admission_number": "562425", "name": "AYUSH BARMAN"},
        {"admission_number": "122425", "name": "BANSHIKA LOHAR"},
        {"admission_number": "452425", "name": "BARBI BARMAN"},
        {"admission_number": "372526", "name": "BIRAJ BARMAN"},
        {"admission_number": "132526", "name": "BRIJRAJ BARMAN"},
        {"admission_number": "392425", "name": "CHIRADEEP ORAON"},
        {"admission_number": "152526", "name": "DEBAPRIYA BARMAN"},
        {"admission_number": "382526", "name": "DIPANKAR JANA"},
        {"admission_number": "462526", "name": "DRISHANT ROY"},
        {"admission_number": "82425", "name": "GOURAB PAUL"},
        {"admission_number": "192526", "name": "ISAN HOSSAIN"},
        {"admission_number": "22526", "name": "ISHIKA ROY"},
        {"admission_number": "222526", "name": "KAYRA KUMARI GIRI"},
        {"admission_number": "162526", "name": "KUNDAN ROY"},
        {"admission_number": "62425", "name": "NASIFA MALIK"},
        {"admission_number": "202425", "name": "PARTHIV SARKAR"},
        {"admission_number": "682425", "name": "PEHAL AGARWAL"},
        {"admission_number": "332425", "name": "PRIANSHI KANTHA"},
        {"admission_number": "362425", "name": "PRIYANSHU SHA"},
        {"admission_number": "42425", "name": "RAHAT HAQUE"},
        {"admission_number": "372425", "name": "RIJBHAN ALAM"},
        {"admission_number": "72526", "name": "RIYANSHI DAS"},
        {"admission_number": "212425", "name": "RUHI ORAON"},
        {"admission_number": "352425", "name": "SAMRIDDHI ROY"},
        {"admission_number": "482425", "name": "SARTHAK DAS"},
        {"admission_number": "272425", "name": "SARTHAK GHOSH DASTIDAR"},
        {"admission_number": "262425", "name": "SOURAV PAUL"},
        {"admission_number": "132425", "name": "SOYTA BARWAR"},
        {"admission_number": "562526", "name": "SUBHOJIT TALUKDAR"},
        {"admission_number": "22425", "name": "YASH ADHIKARI"}
      ],
      "Nursery": [
        {"admission_number": "400", "name": "AISSA ABDA"},
        {"admission_number": "501", "name": "MAHMOUD OUBERI"},
        {"admission_number": "502", "name": "HIBA OUBER"},
        {"admission_number": "503", "name": "CHAYMAE QASMI"},
        {"admission_number": "504", "name": "WALID  ABD"},
        {"admission_number": "82526", "name": "AADIYA ROY"},
        {"admission_number": "272526", "name": "AMRITA ROY"},
        {"admission_number": "282526", "name": "AYUSH MANDAL"},
        {"admission_number": "322526", "name": "BIBHAN CHAKRABORTY"},
        {"admission_number": "362526", "name": "DISHITA BARMAN"},
        {"admission_number": "52526", "name": "DRITI BARMAN"},
        {"admission_number": "572526", "name": "DURJOY DAS"},
        {"admission_number": "342526", "name": "HIYAN PAUL"},
        {"admission_number": "122526", "name": "ISHIKA DAS"},
        {"admission_number": "452526", "name": "JIYANSI BIR"},
        {"admission_number": "42526", "name": "MANDIRA SAHA"},
        {"admission_number": "242526", "name": "NIKHIL SAH"},
        {"admission_number": "232526", "name": "OISHI PAUL"},
        {"admission_number": "112526", "name": "PRIHAN ROY"},
        {"admission_number": "142526", "name": "RIZWAN ISLAM"},
        {"admission_number": "202526", "name": "RONITA CHANDRAMA"},
        {"admission_number": "352526", "name": "RUSHAN PAUL"},
        {"admission_number": "502526", "name": "SANVIKA BARMAN"},
        {"admission_number": "302526", "name": "SAYAN SARKAR"},
        {"admission_number": "62526", "name": "SIMON HANSDA"},
        {"admission_number": "442526", "name": "SRIYANSH LASKAR"},
        {"admission_number": "172526", "name": "TRINANGSHI DUTTA"}
      ]
    };
    
    const totalStudents = Object.values(classStudentMapping).flat().length;
    
    if (!window.confirm(`Excel থেকে ${totalStudents} জন নতুন student import করতে চান?\n\nএটা নতুন Student ID generate করবে (MSMS2025-2026XXXX format এ)`)) {
      return;
    }
    
    setImportingStudents(true);
    setImportResult(null);
    try {
      // Try background job first for large imports
      await startBackgroundJob(
        `/api/jobs/import-students/${selectedSchool}`,
        classStudentMapping,
        'import_students',
        (result) => {
          setImportingStudents(false);
          setImportResult(result);
        }
      );
    } catch (error) {
      // Fallback to direct API call
      try {
        const res = await axios.post(
          `${API_URL}/api/super-admin/tenant/${selectedSchool}/import-students-from-data`,
          classStudentMapping,
          { headers: getAuthHeader() }
        );
        setImportResult(res.data);
        toast.success(res.data.message);
        fetchClassSummary();
        fetchDataSummary(selectedSchool);
        setImportingStudents(false);
      } catch (err) {
        toast.error('Import failed: ' + (err.response?.data?.detail || err.message));
        setImportingStudents(false);
      }
    }
  };

  // Generate Invoices for Promoted Students
  const generateInvoicesForPromoted = async () => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    
    if (!window.confirm('সব active students এর জন্য 2026-2027 academic year এর invoices generate করতে চান?\n\nযাদের আগে থেকে invoice আছে তাদের skip করা হবে।')) {
      return;
    }
    
    setGeneratingInvoices(true);
    setInvoiceResult(null);
    try {
      await startBackgroundJob(
        `/api/jobs/generate-invoices/${selectedSchool}`,
        { academic_year: "2026-2027" },
        'generate_invoices',
        (result) => {
          setGeneratingInvoices(false);
          setInvoiceResult(result);
        }
      );
    } catch (error) {
      toast.error('Failed to start invoice generation: ' + (error.response?.data?.detail || error.message));
      setGeneratingInvoices(false);
    }
  };

  // Delete all payments for school
  const deleteAllPayments = async () => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete ALL fee payments for this school? This will reset all invoices to pending.')) {
      return;
    }
    
    setLoading(true);
    try {
      // First get all payments
      const paymentsRes = await axios.get(
        `${API_URL}/api/fee-management/payments?page_size=10000`,
        { headers: getAuthHeader() }
      );
      
      if (paymentsRes.data.payments && paymentsRes.data.payments.length > 0) {
        const paymentIds = paymentsRes.data.payments.map(p => p.id);
        
        const res = await axios.delete(
          `${API_URL}/api/super-admin/tenant/${selectedSchool}/fee-payments?${paymentIds.map(id => `payment_ids=${id}`).join('&')}`,
          { headers: getAuthHeader() }
        );
        toast.success(res.data.message);
      } else {
        toast.info('No payments found to delete');
      }
      
      fetchDataSummary(selectedSchool);
    } catch (error) {
      toast.error('Failed to delete payments');
    } finally {
      setLoading(false);
    }
  };

  // Delete all concessions/discounts from Fee Settings (templates)
  const deleteAllConcessions = async () => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete ALL concession SETTINGS (templates) from Fee Setup? This will NOT affect discounts already applied to invoices.')) {
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.delete(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/fee-discounts?delete_all=true`,
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message);
      fetchDataSummary(selectedSchool);
    } catch (error) {
      toast.error('Failed to delete concession settings');
    } finally {
      setLoading(false);
    }
  };

  // Delete auto-generated invoices for 2026-2027
  const deleteAutoGeneratedInvoices = async () => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    
    if (!window.confirm('⚠️ WARNING!\n\n2026-2027 academic year এর সব AUTO-GENERATED invoices delete হয়ে যাবে!\n\nএর মধ্যে থাকবে:\n- Promotion এর সময় generate হওয়া invoices\n- Bulk generation এ generate হওয়া invoices\n\nআপনি কি নিশ্চিত?')) {
      return;
    }
    
    setDeletingInvoices(true);
    try {
      const res = await axios.delete(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/auto-generated-invoices?academic_year=2026-2027`,
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message);
      setInvoiceResult(null);
      fetchDataSummary(selectedSchool);
    } catch (error) {
      toast.error('Failed to delete invoices: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeletingInvoices(false);
    }
  };

  // Delete ALL unpaid invoices (not just auto-generated)
  const deleteAllUnpaidInvoices = async () => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    
    if (!window.confirm('⚠️ সাবধান!\n\n2026-2027 academic year এর সব UNPAID invoices delete হয়ে যাবে!\n\nএতে থাকবে:\n- সব pending invoices\n- সব overdue invoices\n- সব upcoming invoices\n\nPaid invoices থাকবে।\n\nআপনি কি নিশ্চিত?')) {
      return;
    }
    
    // Double confirm
    if (!window.confirm('FINAL CONFIRMATION:\n\nসত্যিই সব UNPAID invoices delete করতে চান?')) {
      return;
    }
    
    setDeletingInvoices(true);
    try {
      const res = await axios.delete(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/all-unpaid-invoices?academic_year=2026-2027`,
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message);
      setInvoiceResult(null);
      fetchDataSummary(selectedSchool);
    } catch (error) {
      toast.error('Failed to delete invoices: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeletingInvoices(false);
    }
  };

  // Debug invoices - show what's in database
  const debugInvoices = async () => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    
    try {
      const res = await axios.get(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/debug-invoices`,
        { headers: getAuthHeader() }
      );
      console.log('Invoice Debug:', res.data);
      alert(`Invoice Debug Info:
      
Total Invoices: ${res.data.total_invoices}

Academic Years: ${JSON.stringify(res.data.academic_years)}

Statuses: ${JSON.stringify(res.data.statuses)}

Paid vs Unpaid: Paid=${res.data.paid_vs_unpaid.paid}, Unpaid=${res.data.paid_vs_unpaid.unpaid}

Sample Invoice: ${res.data.sample_invoices[0] ? JSON.stringify(res.data.sample_invoices[0], null, 2) : 'None'}`);
    } catch (error) {
      toast.error('Debug failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Compare students in a class with Excel
  const compareStudentsWithExcel = async (className) => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    
    try {
      const res = await axios.get(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/compare-students?class_name=${encodeURIComponent(className)}`,
        { headers: getAuthHeader() }
      );
      console.log('Compare Students:', res.data);
      
      const students = res.data.students || [];
      let studentList = students.map(s => `${s.name} (Father: ${s.father_name})`).join('\n');
      
      alert(`Students in ${className}:
      
Total in DB: ${res.data.total_students_in_db}

First 30 students:
${studentList}`);
    } catch (error) {
      toast.error('Compare failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Force delete ALL unpaid invoices (no academic year filter)
  const forceDeleteInvoices = async () => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    
    if (!window.confirm('⚠️ FORCE DELETE!\n\nএটা সব UNPAID invoices delete করবে - সব academic year এর!\n\nPaid invoices থাকবে।\n\nআপনি কি নিশ্চিত?')) {
      return;
    }
    
    if (!window.confirm('FINAL CONFIRMATION: Force delete ALL unpaid invoices?')) {
      return;
    }
    
    setDeletingInvoices(true);
    try {
      const res = await axios.delete(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/force-delete-invoices`,
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message);
      setInvoiceResult(null);
      fetchDataSummary(selectedSchool);
    } catch (error) {
      toast.error('Force delete failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeletingInvoices(false);
    }
  };

  // Debug Fee Heads - show promotion settings
  const debugFeeHeads = async () => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    
    try {
      const res = await axios.get(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/debug-fee-heads`,
        { headers: getAuthHeader() }
      );
      console.log('Fee Heads Debug:', res.data);
      
      const promotionFees = res.data.promotion_fees || [];
      const admissionFees = res.data.admission_fees || [];
      
      alert(`Fee Heads Settings:
      
Total Fee Heads: ${res.data.total_fee_heads}

PROMOTION এ Apply হবে (${promotionFees.length}):
${promotionFees.map(f => `• ${f.name} (${f.fee_category || 'one_time'})`).join('\n') || 'কোনো fee নেই!'}

ADMISSION এ Apply হবে (${admissionFees.length}):
${admissionFees.slice(0,10).map(f => `• ${f.name}`).join('\n')}`);
    } catch (error) {
      toast.error('Debug failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Fix Fee Head Promotion Settings
  const fixFeeHeadPromotionSettings = async () => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    
    if (!window.confirm('এটা Fee Heads এর promotion settings ঠিক করবে:\n\n• Monthly fees (Tuition, Transport) → Promotion এ apply হবে\n• Admission fees → Promotion এ apply হবে না\n\nContinue?')) {
      return;
    }
    
    try {
      const res = await axios.post(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/fix-fee-head-promotion`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message);
      
      if (res.data.updates && res.data.updates.length > 0) {
        alert(`Updated ${res.data.updates.length} fee heads:\n\n${res.data.updates.map(u => `${u.name}: ${u.old} → ${u.new}`).join('\n')}`);
      }
    } catch (error) {
      toast.error('Fix failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Reset discounts APPLIED to invoices (the actual discount amounts on fees)
  const resetAppliedDiscounts = async () => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    
    if (!window.confirm('Are you sure you want to reset ALL applied discounts on invoices? This will set discount=0 on all invoices and recalculate balances. Fee Setup concession settings will NOT be affected.')) {
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/reset-all-invoice-discounts`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message);
      fetchDataSummary(selectedSchool);
    } catch (error) {
      toast.error('Failed to reset applied discounts');
    } finally {
      setLoading(false);
    }
  };

  // Delete ALL fee data (invoices, payments, discounts, assignments)
  const deleteAllFeeData = async () => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    
    if (!window.confirm('⚠️ WARNING: This will DELETE ALL fee data including:\n- All Invoices\n- All Payments\n- All Applied Discounts\n- All Fee Assignments\n\nThis cannot be undone! Are you sure?')) {
      return;
    }
    
    // Double confirm
    if (!window.confirm('FINAL CONFIRMATION: Delete ALL fee/invoice data for this school?')) {
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/delete-all-fee-data`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message);
      fetchDataSummary(selectedSchool);
    } catch (error) {
      toast.error('Failed to delete fee data');
    } finally {
      setLoading(false);
    }
  };

  // Update Student Details from Excel Data
  const updateStudentDetailsFromExcel = async () => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    
    if (!window.confirm('এটি Excel থেকে সব students এর details (Father Name, Mother Name, Phone, Address) update করবে।\n\nClass change হবে না, শুধু missing details add হবে।\n\nContinue?')) {
      return;
    }
    
    setUpdatingStudentDetails(true);
    setUpdateDetailsResult(null);
    setActiveJob({ type: 'update-student-details' });
    setJobProgress(0);
    setJobMessage('Starting student details update...');
    
    try {
      // Start the background job
      const res = await axios.post(
        `${API_URL}/api/jobs/update-student-details/${selectedSchool}`,
        {},
        { headers: getAuthHeader() }
      );
      
      const jobId = res.data.job_id;
      toast.info('Student details update started...');
      
      // Start polling
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await axios.get(
            `${API_URL}/api/jobs/${jobId}/status`,
            { headers: getAuthHeader() }
          );
          
          const status = statusRes.data;
          setJobProgress(status.progress || 0);
          setJobMessage(status.message || 'Processing...');
          
          if (status.status === 'completed') {
            clearInterval(pollInterval);
            setUpdatingStudentDetails(false);
            setActiveJob(null);
            setUpdateDetailsResult(status.result);
            toast.success(`✅ ${status.result?.students_updated || 0} students updated!`);
            fetchDataSummary(selectedSchool);
          } else if (status.status === 'failed') {
            clearInterval(pollInterval);
            setUpdatingStudentDetails(false);
            setActiveJob(null);
            toast.error(`❌ Update failed: ${status.error}`);
          }
        } catch (pollError) {
          console.error('Poll error:', pollError);
        }
      }, 2000);
      
      // Store poll interval for cleanup
      pollRef.current = pollInterval;
      
    } catch (error) {
      console.error('Update details error:', error);
      toast.error('Failed to start student details update');
      setUpdatingStudentDetails(false);
      setActiveJob(null);
    }
  };

  const fetchResetHistory = async (schoolId) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/super-admin/tenant/${schoolId}/reset-history`,
        { headers: getAuthHeader() }
      );
      setResetHistory(res.data);
    } catch (error) {
      console.error('Failed to fetch reset history');
    }
  };

  // Fetch accounting summary
  const fetchAccountingSummary = async () => {
    if (!selectedSchool) return;
    setLoadingAccountingSummary(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/accounting-summary`,
        { headers: getAuthHeader() }
      );
      setAccountingSummary(res.data);
    } catch (error) {
      toast.error('Failed to fetch accounting summary');
      console.error(error);
    } finally {
      setLoadingAccountingSummary(false);
    }
  };

  // Delete accounting data
  const handleDeleteAccountingData = async () => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }
    
    if (!window.confirm(`⚠️ এই কাজটি করলে ${accountingDeleteType === 'all' ? 'সব' : 'test/dummy'} accounting data মুছে যাবে! নিশ্চিত?`)) {
      return;
    }
    
    setDeletingAccountingData(true);
    try {
      const res = await axios.delete(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/accounting-data`,
        { 
          headers: getAuthHeader(),
          params: {
            delete_type: accountingDeleteType,
            confirm: true
          }
        }
      );
      
      toast.success(`মুছে ফেলা হয়েছে: ${res.data.total_deleted} entries`);
      setAccountingSummary(null);
      fetchDataSummary(selectedSchool);
    } catch (error) {
      toast.error('Failed to delete accounting data');
      console.error(error);
    } finally {
      setDeletingAccountingData(false);
    }
  };

  // Wrong Invoice Cleanup functions
  const previewWrongInvoices = async () => {
    if (!selectedSchool) return;
    setLoadingWrongInvoices(true);
    try {
      const params = {};
      if (wrongInvoiceDateFilter) params.created_date = wrongInvoiceDateFilter;
      const res = await axios.get(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/cleanup-wrong-invoices`,
        { headers: getAuthHeader(), params }
      );
      setWrongInvoicePreview(res.data);
      if (res.data.total_wrong === 0) {
        toast.info('কোনো wrong invoice পাওয়া যায়নি');
      }
    } catch (error) {
      toast.error('Failed to scan wrong invoices');
    } finally {
      setLoadingWrongInvoices(false);
    }
  };

  const deleteWrongInvoices = async () => {
    if (!selectedSchool) return;
    if (!wrongInvoicePreview || wrongInvoicePreview.deletable === 0) {
      toast.error('No deletable wrong invoices found');
      return;
    }
    setDeletingWrongInvoices(true);
    try {
      const params = { confirm: true };
      if (wrongInvoiceDateFilter) params.created_date = wrongInvoiceDateFilter;
      const res = await axios.delete(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/cleanup-wrong-invoices`,
        { headers: getAuthHeader(), params }
      );
      toast.success(`${res.data.deleted_count} wrong invoices মুছে ফেলা হয়েছে!`);
      setWrongInvoicePreview(null);
      fetchDataSummary(selectedSchool);
    } catch (error) {
      toast.error('Failed to delete wrong invoices');
    } finally {
      setDeletingWrongInvoices(false);
    }
  };

  // Cron Duplicate Cleanup functions
  const previewCronDuplicates = async () => {
    if (!selectedSchool || !cronDupMonth) {
      toast.error('School ও Month select করুন');
      return;
    }
    setLoadingCronDup(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/cleanup-cron-duplicates`,
        { headers: getAuthHeader(), params: { month: cronDupMonth } }
      );
      setCronDupPreview(res.data);
      if (res.data.total === 0) toast.info('কোনো cron duplicate পাওয়া যায়নি');
    } catch (error) {
      toast.error('Failed to scan cron duplicates');
    } finally {
      setLoadingCronDup(false);
    }
  };

  const deleteCronDuplicates = async () => {
    if (!selectedSchool || !cronDupMonth || !cronDupPreview?.deletable) return;
    setDeletingCronDup(true);
    try {
      const res = await axios.delete(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/cleanup-cron-duplicates`,
        { headers: getAuthHeader(), params: { month: cronDupMonth, confirm: true } }
      );
      toast.success(`${res.data.deleted_count} cron duplicate invoices মুছে ফেলা হয়েছে!`);
      setCronDupPreview(null);
      fetchDataSummary(selectedSchool);
    } catch (error) {
      toast.error('Failed to delete cron duplicates');
    } finally {
      setDeletingCronDup(false);
    }
  };

  // One-Time Fee Duplicate Cleanup functions
  const previewOnetimeDuplicates = async () => {
    if (!selectedSchool) { toast.error('Select a school first'); return; }
    setLoadingOnetimeDup(true);
    setOnetimeDupPreview(null);
    try {
      const res = await axios.get(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/duplicate-onetime-fees?confirm_delete=false`,
        { headers: getAuthHeader() }
      );
      setOnetimeDupPreview(res.data);
      if (res.data.stats?.total_duplicates === 0) toast.info('No duplicate one-time fees found');
    } catch (error) {
      toast.error('Failed to scan one-time duplicates');
    } finally {
      setLoadingOnetimeDup(false);
    }
  };

  const deleteOnetimeDuplicates = async () => {
    if (!window.confirm(`Delete ${onetimeDupPreview?.stats?.deletable_unpaid || 0} duplicate one-time fee invoices?`)) return;
    setDeletingOnetimeDup(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/duplicate-onetime-fees?confirm_delete=true`,
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message);
      setOnetimeDupPreview(null);
      fetchDataSummary(selectedSchool);
    } catch (error) {
      toast.error('Failed to delete duplicates');
    } finally {
      setDeletingOnetimeDup(false);
    }
  };

  // Wrong Admission Fee Cleanup functions
  const previewWrongAdmFees = async () => {
    if (!selectedSchool) { toast.error('Select a school first'); return; }
    setLoadingWrongAdm(true);
    setWrongAdmPreview(null);
    try {
      const res = await axios.get(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/wrong-admission-fees?confirm_delete=false`,
        { headers: getAuthHeader() }
      );
      setWrongAdmPreview(res.data);
      if (res.data.stats?.total_wrong === 0) toast.info('No wrong admission fees found');
    } catch (error) {
      toast.error('Failed to scan');
    } finally {
      setLoadingWrongAdm(false);
    }
  };

  const deleteWrongAdmFees = async () => {
    if (!window.confirm(`Delete ${wrongAdmPreview?.stats?.deletable_unpaid || 0} wrong admission fee invoices?`)) return;
    setDeletingWrongAdm(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/wrong-admission-fees?confirm_delete=true`,
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message);
      setWrongAdmPreview(null);
      fetchDataSummary(selectedSchool);
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setDeletingWrongAdm(false);
    }
  };

  // Fractional amount cleanup functions
  const scanFractionalAmounts = async () => {
    if (!selectedSchool) { toast.error('Select a school first'); return; }
    setLoadingFracScan(true);
    setFracScan(null);
    try {
      const params = fracStudentId ? `?student_id=${fracStudentId}` : '';
      const res = await axios.get(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/fractional-amounts${params}`,
        { headers: getAuthHeader() }
      );
      setFracScan(res.data);
      const total = (res.data.invoices_with_fractions || 0) + (res.data.payments_with_fractions || 0);
      if (res.data.message) {
        toast.info(res.data.message);
      } else if (total === 0) {
        toast.info('No fractional amounts found');
      } else {
        toast.success(`Found ${total} records with fractional amounts`);
      }
    } catch (error) {
      toast.error('Failed to scan fractional amounts');
    } finally {
      setLoadingFracScan(false);
    }
  };

  const fixFractionalAmounts = async () => {
    const total = (fracScan?.invoices_with_fractions || 0) + (fracScan?.payments_with_fractions || 0);
    if (!window.confirm(`Round ${total} fractional amounts to the nearest integer? This is irreversible.`)) return;
    setFixingFrac(true);
    try {
      const params = new URLSearchParams({ dry_run: 'false' });
      if (fracStudentId) params.append('student_id', fracStudentId);
      const res = await axios.post(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/fractional-amounts/fix?${params.toString()}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message);
      setFracScan(null);
    } catch (error) {
      toast.error('Failed to fix fractional amounts');
    } finally {
      setFixingFrac(false);
    }
  };

  // Duplicate one-time invoice cleanup
  const scanDupOneTime = async () => {
    if (!selectedSchool) { toast.error('Select a school first'); return; }
    setLoadingDupOneTime(true);
    setDupOneTimePreview(null);
    try {
      const params = dupOneTimeStudentId ? `?student_id=${dupOneTimeStudentId}` : '';
      const res = await axios.get(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/duplicate-one-time-invoices${params}`,
        { headers: getAuthHeader() }
      );
      setDupOneTimePreview(res.data);
      const total = res.data.stats?.total_deletable || 0;
      if (total === 0) toast.info('No duplicate one-time invoices found');
      else toast.success(`Found ${total} deletable duplicates across ${res.data.stats?.affected_students || 0} students`);
    } catch (error) {
      toast.error('Scan failed');
    } finally {
      setLoadingDupOneTime(false);
    }
  };

  const deleteDupOneTime = async () => {
    const total = dupOneTimePreview?.stats?.total_deletable || 0;
    if (!window.confirm(`Permanently delete ${total} duplicate one-time fee invoices? This cannot be undone.`)) return;
    setDeletingDupOneTime(true);
    try {
      const params = new URLSearchParams({ confirm_delete: 'true' });
      if (dupOneTimeStudentId) params.append('student_id', dupOneTimeStudentId);
      const res = await axios.get(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/duplicate-one-time-invoices?${params.toString()}`,
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message);
      setDupOneTimePreview(null);
      fetchDataSummary(selectedSchool);
    } catch (error) {
      toast.error('Delete failed');
    } finally {
      setDeletingDupOneTime(false);
    }
  };

  const handleSchoolChange = (schoolId) => {
    setSelectedSchool(schoolId);
    setDataSummary(null);
    setSelectedCollections([]);
    setMoveResults(null);
    setSearchResults(null);
    setClassSummary(null);
    setSchoolClasses([]); // Clear old classes
    setTargetClassId(''); // Clear selected class
    setWrongInvoicePreview(null);
    setCronDupPreview(null);
    if (schoolId) {
      fetchDataSummary(schoolId);
      fetchResetHistory(schoolId);
      fetchSchoolClasses(schoolId); // Pass schoolId directly
    }
  };

  const toggleCollection = (collection) => {
    setSelectedCollections(prev => 
      prev.includes(collection) 
        ? prev.filter(c => c !== collection)
        : [...prev, collection]
    );
  };

  const selectGroup = (group) => {
    const groupCollections = COLLECTION_GROUPS[group];
    const allSelected = groupCollections.every(c => selectedCollections.includes(c));
    
    if (allSelected) {
      setSelectedCollections(prev => prev.filter(c => !groupCollections.includes(c)));
    } else {
      setSelectedCollections(prev => [...new Set([...prev, ...groupCollections])]);
    }
  };

  const handleReset = async () => {
    if (confirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        reset_type: resetType,
        confirm: 'true'
      });
      
      if (resetType === 'partial' && selectedCollections.length > 0) {
        selectedCollections.forEach(c => params.append('collections', c));
      }

      const res = await axios.post(
        `${API_URL}/api/super-admin/tenant/${selectedSchool}/reset?${params.toString()}`,
        {},
        { headers: getAuthHeader() }
      );
      
      toast.success(`Deleted ${res.data.total_deleted} records`);
      setShowConfirmDialog(false);
      setConfirmText('');
      fetchDataSummary(selectedSchool);
      fetchResetHistory(selectedSchool);
    } catch (error) {
      toast.error('Failed to reset data');
    } finally {
      setLoading(false);
    }
  };

  const getCollectionIcon = (collection) => {
    if (['students', 'teachers', 'staff', 'users'].includes(collection)) return <Users className="h-4 w-4" />;
    if (['fees', 'fee_payments', 'salary'].includes(collection)) return <DollarSign className="h-4 w-4" />;
    if (['exams', 'marks'].includes(collection)) return <BookOpen className="h-4 w-4" />;
    if (['attendance'].includes(collection)) return <Calendar className="h-4 w-4" />;
    return <Database className="h-4 w-4" />;
  };

  return (
    <Card className="border-red-200 dark:border-red-900" data-testid="tenant-reset-manager">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-red-600">
          <ShieldAlert className="h-5 w-5" />
          Tenant Data Reset (Super Admin)
        </CardTitle>
        <CardDescription>
          Reset all or partial data for a school tenant. This action is IRREVERSIBLE.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* School Selector */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Select School</label>
          <Select value={selectedSchool} onValueChange={handleSchoolChange}>
            <SelectTrigger data-testid="school-selector">
              <SelectValue placeholder="Choose a school..." />
            </SelectTrigger>
            <SelectContent>
              {schools.map(school => (
                <SelectItem key={school.id} value={school.id}>
                  {school.name} ({school.school_code || school.code || 'N/A'})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Data Summary */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        )}

        {dataSummary && !loading && (
          <>
            {/* Summary Stats */}
            <div className="p-4 bg-muted rounded-lg">
              <p className="font-medium">{dataSummary.school?.name}</p>
              <p className="text-2xl font-bold text-red-600">{dataSummary.total_records.toLocaleString()} Total Records</p>
            </div>

            {/* Quick Actions for Finance */}
            <div className="p-4 border border-orange-200 dark:border-orange-900 rounded-lg bg-orange-50 dark:bg-orange-950">
              <h4 className="font-medium text-orange-700 dark:text-orange-300 mb-3">Quick Actions (Finance)</h4>
              <div className="flex flex-wrap gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={recalculateStudentFees}
                  disabled={recalculating}
                  className="border-orange-300 text-orange-700 hover:bg-orange-100"
                >
                  {recalculating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Recalculate All Fee Balances
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={deleteAllPayments}
                  disabled={loading}
                  className="border-red-300 text-red-700 hover:bg-red-100"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete All Payments
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={resetAppliedDiscounts}
                  disabled={loading}
                  className="border-purple-300 text-purple-700 hover:bg-purple-100"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Reset Applied Discounts
                </Button>
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={deleteAllFeeData}
                  disabled={loading}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  DELETE ALL FEE DATA
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                <strong className="text-red-600">DELETE ALL FEE DATA</strong>: Removes ALL invoices, payments, discounts, and fee assignments. Use with caution!
              </p>
            </div>

            {/* Find Students Section */}
            <div className="p-4 border border-blue-200 dark:border-blue-900 rounded-lg bg-blue-50 dark:bg-blue-950">
              <h4 className="font-medium text-blue-700 dark:text-blue-300 mb-3 flex items-center gap-2">
                <UserSearch className="h-5 w-5" />
                Find Students (Lost After Promotion)
              </h4>
              
              <div className="flex flex-wrap gap-2 mb-4">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={fetchClassSummary}
                  disabled={loadingClassSummary}
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                  data-testid="view-class-summary-btn"
                >
                  {loadingClassSummary ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
                  View Class Summary
                </Button>
              </div>

              {/* Search by Name */}
              <div className="flex gap-2 mb-4">
                <Input
                  placeholder="Enter student name to search..."
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchStudents()}
                  className="max-w-xs"
                  data-testid="search-student-name-input"
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={searchStudents}
                  disabled={searchingStudents}
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                  data-testid="search-student-btn"
                >
                  {searchingStudents ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                  Search
                </Button>
              </div>

              {/* Class Summary */}
              {showFindStudents && classSummary && (
                <div className="mt-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <h5 className="font-medium text-sm">Class-wise Student Count</h5>
                    <Badge variant="secondary">
                      Total: {classSummary.total_students} students | Promoted: {classSummary.total_promoted}
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {classSummary.classes && classSummary.classes.map((cls) => (
                      <div 
                        key={cls.class_id}
                        className="p-2 border rounded bg-white dark:bg-gray-800 cursor-pointer hover:border-blue-400"
                        onClick={() => searchAllStudents(cls.class_id)}
                        data-testid={`class-card-${cls.class_name}`}
                      >
                        <p className="font-medium text-sm">{cls.class_name}</p>
                        <p className="text-lg font-bold text-blue-600">{cls.total_students}</p>
                        {cls.promoted_students > 0 && (
                          <Badge variant="outline" className="text-xs">
                            {cls.promoted_students} promoted
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Search Results */}
              {searchResults && (
                <div className="mt-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <h5 className="font-medium text-sm">Search Results</h5>
                    <Badge variant="secondary">Found: {searchResults.total_found} students</Badge>
                  </div>
                  
                  {searchResults.by_class && Object.keys(searchResults.by_class).length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(searchResults.by_class).map(([className, students]) => (
                        <div key={className} className="border rounded p-3 bg-white dark:bg-gray-800">
                          <h6 className="font-medium text-sm mb-2 flex items-center gap-2">
                            <GraduationCap className="h-4 w-4" />
                            {className} ({students.length} students)
                          </h6>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b">
                                  <th className="text-left p-1">Name</th>
                                  <th className="text-left p-1">Student ID</th>
                                  <th className="text-left p-1">Status</th>
                                  <th className="text-left p-1">Promotion</th>
                                </tr>
                              </thead>
                              <tbody>
                                {students.map((student) => (
                                  <tr key={student.id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td className="p-1 font-medium">{student.name}</td>
                                    <td className="p-1 text-muted-foreground">{student.student_id || '-'}</td>
                                    <td className="p-1">
                                      <Badge variant={student.status === 'active' ? 'default' : 'secondary'}>
                                        {student.status || 'active'}
                                      </Badge>
                                    </td>
                                    <td className="p-1">
                                      <Badge 
                                        variant={student.promotion_status === 'promoted' ? 'default' : 'outline'}
                                        className={student.promotion_status === 'promoted' ? 'bg-green-500' : ''}
                                      >
                                        {student.promotion_status || 'none'}
                                      </Badge>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No students found</p>
                  )}
                </div>
              )}
            </div>

            {/* Move Students to Class Section */}
            <div className="p-4 border border-green-200 dark:border-green-900 rounded-lg bg-green-50 dark:bg-green-950">
              <h4 className="font-medium text-green-700 dark:text-green-300 mb-3 flex items-center gap-2">
                <RefreshCw className="h-5 w-5" />
                Move Students to Different Class
              </h4>
              <p className="text-sm text-muted-foreground mb-4">
                Use this to fix students stuck in wrong class after promotion issues.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {/* Target Class Selection */}
                <div>
                  <label className="text-sm font-medium mb-1 block">Target Class (Move To)</label>
                  <Select value={targetClassId} onValueChange={setTargetClassId}>
                    <SelectTrigger data-testid="target-class-selector">
                      <SelectValue placeholder="Select target class..." />
                    </SelectTrigger>
                    <SelectContent>
                      {schoolClasses.map(cls => (
                        <SelectItem key={cls.id} value={cls.id}>
                          {cls.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Admission Numbers Input */}
                <div>
                  <label className="text-sm font-medium mb-1 block">Admission Numbers (one per line or comma separated)</label>
                  <textarea
                    placeholder="MSMS2025-20260572&#10;MSMS2025-20260573&#10;MSMS2025-20260574"
                    value={admissionNumbers}
                    onChange={(e) => setAdmissionNumbers(e.target.value)}
                    className="w-full h-24 p-2 border rounded text-sm font-mono"
                    data-testid="admission-numbers-input"
                  />
                </div>
              </div>
              
              <Button 
                variant="default" 
                size="sm"
                onClick={moveStudentsToClass}
                disabled={movingStudents || !targetClassId || !admissionNumbers.trim()}
                className="bg-green-600 hover:bg-green-700"
                data-testid="move-students-btn"
              >
                {movingStudents ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Move Students to Selected Class
              </Button>
              
              {/* Move Results */}
              {moveResults && (
                <div className="mt-4 p-3 border rounded bg-white dark:bg-gray-800">
                  <h5 className="font-medium text-sm mb-2">Move Results: {moveResults.target_class}</h5>
                  
                  {moveResults.moved && moveResults.moved.length > 0 && (
                    <div className="mb-2">
                      <p className="text-sm text-green-600 font-medium">Successfully Moved ({moveResults.moved.length}):</p>
                      <ul className="text-sm text-muted-foreground">
                        {moveResults.moved.map((s, idx) => (
                          <li key={idx}>• {s.name} ({s.student_id}): {s.from_class} → {s.to_class}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {moveResults.not_found && moveResults.not_found.length > 0 && (
                    <div className="mb-2">
                      <p className="text-sm text-amber-600 font-medium">Not Found ({moveResults.not_found.length}):</p>
                      <ul className="text-sm text-muted-foreground">
                        {moveResults.not_found.map((s, idx) => (
                          <li key={idx}>• {s}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {moveResults.errors && moveResults.errors.length > 0 && (
                    <div>
                      <p className="text-sm text-red-600 font-medium">Errors ({moveResults.errors.length}):</p>
                      <ul className="text-sm text-muted-foreground">
                        {moveResults.errors.map((e, idx) => (
                          <li key={idx}>• {e}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Delete Students by Class Section */}
            <div className="p-4 border-2 border-red-300 dark:border-red-800 rounded-lg bg-red-50 dark:bg-red-950/50">
              <h4 className="font-medium text-red-700 dark:text-red-300 mb-3 flex items-center gap-2">
                <Trash2 className="h-5 w-5" />
                Delete Students by Class
              </h4>
              <p className="text-sm text-muted-foreground mb-4">
                Delete all students from a specific class. This also deletes their invoices, payments, and other data.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Select Class to Delete From</label>
                  <Select value={deleteByClassId} onValueChange={(v) => { setDeleteByClassId(v); setDeleteByClassPreview(null); }}>
                    <SelectTrigger data-testid="delete-class-selector">
                      <SelectValue placeholder="Select class..." />
                    </SelectTrigger>
                    <SelectContent>
                      {schoolClasses.map(cls => (
                        <SelectItem key={cls.id} value={cls.id}>
                          {cls.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2">
                  <Button
                    variant="outline"
                    onClick={previewDeleteByClass}
                    disabled={!deleteByClassId || deletingByClass}
                    data-testid="preview-delete-class-btn"
                  >
                    {deletingByClass ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                    Preview
                  </Button>
                </div>
              </div>

              {/* Preview Results */}
              {deleteByClassPreview && (
                <div className="mt-4 p-4 border rounded bg-white dark:bg-gray-800">
                  <h5 className="font-bold text-red-600 mb-2">
                    ⚠️ {deleteByClassPreview.class_name}: {deleteByClassPreview.student_count} students will be deleted
                  </h5>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                    <div>
                      <span className="text-muted-foreground">Invoices to delete:</span>{' '}
                      <span className="font-medium">{deleteByClassPreview.related_data?.invoices || 0}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Payments to delete:</span>{' '}
                      <span className="font-medium">{deleteByClassPreview.related_data?.payments || 0}</span>
                    </div>
                  </div>

                  {deleteByClassPreview.students && deleteByClassPreview.students.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm font-medium mb-2">Students to be deleted (first 20):</p>
                      <div className="max-h-40 overflow-y-auto text-sm text-muted-foreground">
                        {deleteByClassPreview.students.map((s, i) => (
                          <div key={i}>• {s.name} ({s.admission_number})</div>
                        ))}
                      </div>
                    </div>
                  )}

                  <Button
                    variant="destructive"
                    onClick={executeDeleteByClass}
                    disabled={deletingByClass}
                    className="w-full"
                    data-testid="execute-delete-class-btn"
                  >
                    {deletingByClass ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                    DELETE {deleteByClassPreview.student_count} STUDENTS
                  </Button>
                </div>
              )}
            </div>

            {/* Delete Payment Section */}
            <div className="p-4 border-2 border-orange-300 dark:border-orange-800 rounded-lg bg-orange-50 dark:bg-orange-950/50">
              <h4 className="font-medium text-orange-700 dark:text-orange-300 mb-3 flex items-center gap-2">
                <Trash2 className="h-5 w-5" />
                Delete Payment / Receipt / Store Sale
              </h4>
              <p className="text-sm text-muted-foreground mb-4">
                Delete a specific payment, fee receipt, or store sale (BILL*). For store sales, stock will be restored and accounting entries reversed.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Payment ID or Receipt Number</label>
                  <Input
                    value={deletePaymentId}
                    onChange={(e) => { setDeletePaymentId(e.target.value); setDeletePaymentPreview(null); }}
                    placeholder="e.g., RCP/20260323/0005 or BILL202604100013"
                    data-testid="delete-payment-input"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <Button
                    variant="outline"
                    onClick={previewDeletePayment}
                    disabled={!deletePaymentId.trim() || deletingPayment}
                    data-testid="preview-delete-payment-btn"
                  >
                    {deletingPayment ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                    Preview
                  </Button>
                </div>
              </div>

              {/* Preview Results */}
              {deletePaymentPreview && deletePaymentPreview.status === 'preview' && (
                <div className="mt-4 p-4 border rounded bg-white dark:bg-gray-800">
                  <h5 className="font-bold text-orange-600 mb-2">
                    {deletePaymentPreview.type === 'store_sale' ? '🛒 Store Sale to be deleted:' : '⚠️ Payment to be deleted:'}
                  </h5>
                  
                  {deletePaymentPreview.type === 'store_sale' && (
                    <div className="mb-3 px-2 py-1 bg-blue-50 dark:bg-blue-900/30 rounded text-xs font-medium text-blue-700 dark:text-blue-300 inline-block">
                      STORE SALE ({deletePaymentPreview.payment?.items_count} items)
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                    <div>
                      <span className="text-muted-foreground">Receipt:</span>{' '}
                      <span className="font-medium">{deletePaymentPreview.payment?.receipt_number}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Amount:</span>{' '}
                      <span className="font-medium text-red-600">₹{deletePaymentPreview.payment?.amount?.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Student:</span>{' '}
                      <span className="font-medium">{deletePaymentPreview.payment?.student_name || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Date:</span>{' '}
                      <span className="font-medium">{deletePaymentPreview.payment?.payment_date}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Mode:</span>{' '}
                      <span className="font-medium uppercase">{deletePaymentPreview.payment?.payment_mode}</span>
                    </div>
                  </div>

                  {/* Stock Restore Info (Store Sales only) */}
                  {deletePaymentPreview.type === 'store_sale' && deletePaymentPreview.stock_to_restore?.length > 0 && (
                    <div className="text-sm mb-4 p-2 bg-green-50 dark:bg-green-900/30 rounded">
                      <p className="font-medium text-green-700 dark:text-green-300 mb-1">Stock to be RESTORED:</p>
                      <ul className="list-disc list-inside text-muted-foreground">
                        {deletePaymentPreview.stock_to_restore.map((item, i) => (
                          <li key={i}>{item.item_name} (Size: {item.size}) — +{item.quantity} units</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="text-sm mb-4 p-2 bg-red-50 dark:bg-red-900/30 rounded">
                    <p className="font-medium text-red-700 dark:text-red-300">Related entries to be deleted:</p>
                    <ul className="list-disc list-inside text-muted-foreground">
                      <li>Journal Entries: {deletePaymentPreview.related_entries?.journal_entries || 0}</li>
                      <li>Cash Entries: {deletePaymentPreview.related_entries?.cash_entries || 0}</li>
                      <li>Bank Entries: {deletePaymentPreview.related_entries?.bank_entries || 0}</li>
                      {deletePaymentPreview.related_entries?.accounting_entries > 0 && (
                        <li>Accounting Entries: {deletePaymentPreview.related_entries?.accounting_entries}</li>
                      )}
                      {deletePaymentPreview.related_entries?.student_fee_entries > 0 && (
                        <li>Student Fee Entries: {deletePaymentPreview.related_entries?.student_fee_entries}</li>
                      )}
                    </ul>
                  </div>

                  <Button
                    variant="destructive"
                    onClick={executeDeletePayment}
                    disabled={deletingPayment}
                    className="w-full"
                    data-testid="execute-delete-payment-btn"
                  >
                    {deletingPayment ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                    {deletePaymentPreview.type === 'store_sale' 
                      ? `DELETE SALE & RESTORE STOCK ₹${deletePaymentPreview.payment?.amount?.toLocaleString()}`
                      : `DELETE PAYMENT ₹${deletePaymentPreview.payment?.amount?.toLocaleString()}`
                    }
                  </Button>
                </div>
              )}
            </div>

            {/* Delete Single Student Section */}
            <div className="p-4 border-2 border-pink-300 dark:border-pink-800 rounded-lg bg-pink-50 dark:bg-pink-950/50">
              <h4 className="font-medium text-pink-700 dark:text-pink-300 mb-3 flex items-center gap-2">
                <UserMinus className="h-5 w-5" />
                Delete Single Student
              </h4>
              <p className="text-sm text-muted-foreground mb-4">
                Delete a specific student and all their data (invoices, payments, etc.)
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Student ID or Admission Number</label>
                  <Input
                    value={deleteStudentId}
                    onChange={(e) => { setDeleteStudentId(e.target.value); setDeleteStudentPreview(null); }}
                    placeholder="e.g., MSMS/2026/001 or student-uuid"
                    data-testid="delete-student-input"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <Button
                    variant="outline"
                    onClick={previewDeleteStudent}
                    disabled={!deleteStudentId.trim() || deletingStudent}
                    data-testid="preview-delete-student-btn"
                  >
                    {deletingStudent ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                    Preview
                  </Button>
                </div>
              </div>

              {/* Preview Results */}
              {deleteStudentPreview && deleteStudentPreview.status === 'preview' && (
                <div className="mt-4 p-4 border rounded bg-white dark:bg-gray-800">
                  <h5 className="font-bold text-pink-600 mb-2">
                    ⚠️ Student to be deleted:
                  </h5>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                    <div>
                      <span className="text-muted-foreground">Name:</span>{' '}
                      <span className="font-medium">{deleteStudentPreview.student?.name}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Admission No:</span>{' '}
                      <span className="font-medium">{deleteStudentPreview.student?.admission_number}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Class:</span>{' '}
                      <span className="font-medium">{deleteStudentPreview.student?.class_name}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Father:</span>{' '}
                      <span className="font-medium">{deleteStudentPreview.student?.father_name}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Phone:</span>{' '}
                      <span className="font-medium">{deleteStudentPreview.student?.phone}</span>
                    </div>
                  </div>

                  <div className="text-sm mb-4 p-2 bg-red-50 dark:bg-red-900/30 rounded">
                    <p className="font-medium text-red-700 dark:text-red-300">Related data to be deleted:</p>
                    <ul className="list-disc list-inside text-muted-foreground">
                      <li>Invoices: {deleteStudentPreview.related_data?.invoices || 0}</li>
                      <li>Payments: {deleteStudentPreview.related_data?.payments || 0}</li>
                      <li>Total Paid: ₹{deleteStudentPreview.related_data?.total_amount_paid?.toLocaleString() || 0}</li>
                    </ul>
                  </div>

                  <Button
                    variant="destructive"
                    onClick={executeDeleteStudent}
                    disabled={deletingStudent}
                    className="w-full"
                    data-testid="execute-delete-student-btn"
                  >
                    {deletingStudent ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                    DELETE STUDENT
                  </Button>
                </div>
              )}
            </div>

            {/* EMERGENCY: Reset Promotions Section */}
            <div className="p-4 border-2 border-red-500 rounded-lg bg-red-50 dark:bg-red-950/30">
              <h4 className="font-bold text-red-700 dark:text-red-300 mb-3 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                EMERGENCY: Fix Promotion Issues
              </h4>
              <p className="text-sm text-muted-foreground mb-4">
                Use these tools if bulk promotion caused data corruption.
              </p>
              
              <div className="space-y-6">
                {/* Background Job Progress Indicator */}
                {activeJob && (
                  <div className="p-4 border-2 border-blue-500 rounded bg-blue-50 dark:bg-blue-900/30">
                    <div className="flex items-center gap-2 mb-2">
                      <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                      <h5 className="font-bold text-blue-700">
                        {activeJob.type === 'delete_students' && 'Deleting Students...'}
                        {activeJob.type === 'import_students' && 'Importing Students...'}
                        {activeJob.type === 'demote_all' && 'Demoting Students...'}
                      </h5>
                    </div>
                    <Progress value={jobProgress} className="h-3 mb-2" />
                    <p className="text-sm text-blue-600">{jobMessage || 'Processing...'} ({jobProgress}%)</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      এই প্রক্রিয়া Background এ চলছে। Page refresh করলেও চলতে থাকবে।
                    </p>
                  </div>
                )}

                {/* DEMOTE ALL - MOST IMPORTANT */}
                <div className="p-4 border-4 border-red-600 rounded bg-red-100 dark:bg-red-900/50">
                  <h5 className="font-bold text-red-700 text-lg mb-2">🔴 DEMOTE ALL - সব Student এক Class পিছিয়ে আনুন</h5>
                  <p className="text-sm text-muted-foreground mb-3">
                    সব students কে এক class পিছিয়ে দেবে (Class 10→9, Class 11→10, etc.)
                    <br/>সাথে সব 2026-2027 এর invoices ও promotion history delete করবে।
                  </p>
                  
                  <Button 
                    variant="destructive" 
                    size="lg"
                    onClick={demoteAllOneClass}
                    disabled={demotingAll}
                    className="bg-red-700 hover:bg-red-800 w-full md:w-auto"
                    data-testid="demote-all-btn"
                  >
                    {demotingAll ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <AlertTriangle className="h-5 w-5 mr-2" />}
                    DEMOTE ALL - সবাইকে এক Class পিছিয়ে আনুন
                  </Button>
                  
                  {demoteResult && (
                    <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded text-sm">
                      <p className="font-bold text-green-600">{demoteResult.message}</p>
                      <div className="mt-2 text-xs">
                        <p>Students demoted: {demoteResult.demoted}</p>
                        <p>Stayed in first class: {demoteResult.stayed_in_first_class}</p>
                        <p>Invoices deleted: {demoteResult.invoices_deleted}</p>
                      </div>
                      {demoteResult.class_mapping && (
                        <div className="mt-2">
                          <p className="font-medium">Class Mapping Used:</p>
                          <ul className="text-xs">
                            {demoteResult.class_mapping.map((m, i) => (
                              <li key={i}>{m.from} → {m.to}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* RESTORE FROM EXCEL - Most Important for User */}
                <div className="p-4 border-4 border-green-600 rounded bg-green-100 dark:bg-green-900/50">
                  <h5 className="font-bold text-green-700 text-lg mb-2">📊 RESTORE FROM EXCEL - আপনার দেওয়া Data থেকে Fix করুন</h5>
                  <p className="text-sm text-muted-foreground mb-3">
                    আপনি যে Excel files দিয়েছেন সেই data থেকে সব students সঠিক class এ restore করবে।
                    <br/><strong>Total: 368 students</strong> (Nursery: 27, LKG: 38, UKG: 41, Class 1-10: 262)
                  </p>
                  
                  <Button 
                    variant="default" 
                    size="lg"
                    onClick={restoreFromExcelData}
                    disabled={restoringFromExcel}
                    className="bg-green-700 hover:bg-green-800 w-full md:w-auto"
                    data-testid="restore-from-excel-btn"
                  >
                    {restoringFromExcel ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <RefreshCw className="h-5 w-5 mr-2" />}
                    RESTORE FROM EXCEL - সব Students সঠিক Class এ আনুন
                  </Button>
                  
                  {restoreResult && (
                    <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded text-sm">
                      <p className="font-bold text-green-600">{restoreResult.message}</p>
                      <div className="mt-2 text-xs">
                        <p>Total processed: {restoreResult.total_processed}</p>
                        <p>Updated: {restoreResult.updated}</p>
                        <p>Not found: {restoreResult.not_found?.length || 0}</p>
                      </div>
                      {restoreResult.not_found && restoreResult.not_found.length > 0 && (
                        <div className="mt-2 max-h-32 overflow-y-auto">
                          <p className="font-medium text-amber-600">Not found in database:</p>
                          <ul className="text-xs">
                            {restoreResult.not_found.slice(0, 20).map((s, i) => (
                              <li key={i}>{s}</li>
                            ))}
                            {restoreResult.not_found.length > 20 && (
                              <li>... and {restoreResult.not_found.length - 20} more</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* DELETE ALL + IMPORT NEW - Fresh Start */}
                <div className="p-4 border-4 border-purple-600 rounded bg-purple-100 dark:bg-purple-900/50">
                  <h5 className="font-bold text-purple-700 text-lg mb-2">🔄 FRESH START - Delete All & Import New Students</h5>
                  <p className="text-sm text-muted-foreground mb-3">
                    সব students delete করে Excel থেকে নতুন করে import করুন।
                    <br/><strong>Step 1:</strong> আগে সব students delete করুন
                    <br/><strong>Step 2:</strong> তারপর Excel থেকে import করুন
                  </p>
                  
                  <div className="flex flex-wrap gap-3 mb-3">
                    <Button 
                      variant="destructive" 
                      size="lg"
                      onClick={deleteAllStudents}
                      disabled={deletingAllStudents}
                      className="bg-red-700 hover:bg-red-800"
                      data-testid="delete-all-students-btn"
                    >
                      {deletingAllStudents ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Trash2 className="h-5 w-5 mr-2" />}
                      Step 1: DELETE ALL STUDENTS
                    </Button>
                    
                    <Button 
                      variant="default" 
                      size="lg"
                      onClick={importStudentsFromExcel}
                      disabled={importingStudents}
                      className="bg-purple-700 hover:bg-purple-800"
                      data-testid="import-students-btn"
                    >
                      {importingStudents ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Users className="h-5 w-5 mr-2" />}
                      Step 2: IMPORT 368 Students from Excel
                    </Button>
                  </div>
                  
                  {importResult && (
                    <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded text-sm">
                      <p className="font-bold text-purple-600">{importResult.message}</p>
                      <div className="mt-2 text-xs">
                        <p>Total processed: {importResult.total_processed}</p>
                        <p>Created: {importResult.created}</p>
                        <p>Skipped (duplicate): {importResult.skipped_duplicate}</p>
                        <p>Last Student ID: {importResult.last_student_id}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* GENERATE INVOICES - After Promotion */}
                <div className="p-4 border-4 border-blue-600 rounded bg-blue-100 dark:bg-blue-900/50">
                  <h5 className="font-bold text-blue-700 text-lg mb-2">💰 GENERATE INVOICES - Promoted Students এর জন্য</h5>
                  <p className="text-sm text-muted-foreground mb-3">
                    Promotion হয়ে গেছে কিন্তু Invoice generate হয়নি? এই button দিয়ে সব active students এর জন্য invoices generate করুন।
                  </p>
                  
                  <Button 
                    variant="default" 
                    size="lg"
                    onClick={generateInvoicesForPromoted}
                    disabled={generatingInvoices}
                    className="bg-blue-700 hover:bg-blue-800"
                    data-testid="generate-invoices-btn"
                  >
                    {generatingInvoices ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <DollarSign className="h-5 w-5 mr-2" />}
                    GENERATE INVOICES (2026-2027)
                  </Button>
                  
                  {invoiceResult && (
                    <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded text-sm">
                      <p className="font-bold text-blue-600">{invoiceResult.message}</p>
                      <div className="mt-2 text-xs">
                        <p>Total Students: {invoiceResult.total_students}</p>
                        <p>Invoices Created: {invoiceResult.invoices_created}</p>
                        <p>Skipped (already existed): {invoiceResult.skipped_existing}</p>
                        {invoiceResult.skipped_admission_fee > 0 && (
                          <p className="text-green-600">Admission Fees Skipped: {invoiceResult.skipped_admission_fee}</p>
                        )}
                      </div>
                      {invoiceResult.by_fee_head && Object.keys(invoiceResult.by_fee_head).length > 0 && (
                        <div className="mt-2">
                          <p className="font-medium">By Fee Head:</p>
                          <ul className="text-xs">
                            {Object.entries(invoiceResult.by_fee_head).map(([name, count]) => (
                              <li key={name}>• {name}: {count}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Delete Auto-Generated Invoices */}
                  <div className="mt-4 p-3 border border-red-300 rounded bg-red-50 dark:bg-red-950/30">
                    <h6 className="font-bold text-red-700 text-sm mb-2">⚠️ ভুল Invoices Delete করুন</h6>
                    <p className="text-xs text-muted-foreground mb-2">
                      আগের auto-generated invoices ভুল হলে এখান থেকে delete করুন, তারপর আবার generate করুন।
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={deleteAutoGeneratedInvoices}
                        disabled={deletingInvoices}
                        data-testid="delete-auto-invoices-btn"
                      >
                        {deletingInvoices ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                        DELETE Auto-Generated Only
                      </Button>
                      
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={deleteAllUnpaidInvoices}
                        disabled={deletingInvoices}
                        data-testid="delete-all-unpaid-invoices-btn"
                        className="bg-red-800 hover:bg-red-900"
                      >
                        {deletingInvoices ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                        DELETE ALL UNPAID (2026-2027)
                      </Button>
                      
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={debugInvoices}
                        className="border-yellow-500 text-yellow-700"
                      >
                        🔍 DEBUG
                      </Button>
                      
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={forceDeleteInvoices}
                        disabled={deletingInvoices}
                        className="bg-black hover:bg-gray-900"
                      >
                        {deletingInvoices ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                        FORCE DELETE ALL
                      </Button>
                    </div>
                    <p className="text-xs text-red-600 mt-2">
                      ⚠️ "FORCE DELETE ALL" সব year এর unpaid invoices delete করবে
                    </p>
                  </div>
                </div>

                {/* FEE HEAD PROMOTION SETTINGS */}
                <div className="p-4 border-4 border-purple-600 rounded bg-purple-100 dark:bg-purple-900/50">
                  <h5 className="font-bold text-purple-700 text-lg mb-2">⚙️ FEE HEAD PROMOTION SETTINGS</h5>
                  <p className="text-sm text-muted-foreground mb-3">
                    কোন fees Promotion এ apply হবে সেটা check ও fix করুন।
                    <br />
                    <span className="text-xs text-purple-600 font-medium">
                      Admission Fee, Form Fee → Promotion এ apply হবে না
                      <br />
                      Tuition Fee, Transport Fee → Promotion এ apply হবে
                    </span>
                  </p>
                  
                  <div className="flex gap-2 flex-wrap">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={debugFeeHeads}
                      className="border-purple-500 text-purple-700"
                    >
                      🔍 CHECK FEE SETTINGS
                    </Button>
                    
                    <Button 
                      variant="default" 
                      size="sm"
                      onClick={fixFeeHeadPromotionSettings}
                      className="bg-purple-700 hover:bg-purple-800"
                    >
                      🔧 FIX PROMOTION SETTINGS
                    </Button>
                  </div>
                </div>

                {/* UPDATE STUDENT DETAILS FROM EXCEL */}
                <div className="p-4 border-4 border-green-600 rounded bg-green-100 dark:bg-green-900/50">
                  <h5 className="font-bold text-green-700 text-lg mb-2">📝 UPDATE STUDENT DETAILS - Excel থেকে</h5>
                  <p className="text-sm text-muted-foreground mb-3">
                    Students এর Father Name, Mother Name, Phone, Address update করুন Excel data থেকে।
                    <br />
                    <span className="text-xs text-green-600 font-medium">✅ Class change হবে না, শুধু missing details add হবে।</span>
                  </p>
                  
                  <Button 
                    variant="default" 
                    size="lg"
                    onClick={updateStudentDetailsFromExcel}
                    disabled={updatingStudentDetails}
                    className="bg-green-700 hover:bg-green-800"
                    data-testid="update-student-details-btn"
                  >
                    {updatingStudentDetails ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Users className="h-5 w-5 mr-2" />}
                    UPDATE STUDENT DETAILS
                  </Button>
                  
                  {/* Progress indicator */}
                  {updatingStudentDetails && activeJob?.type === 'update-student-details' && (
                    <div className="mt-3">
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div 
                          className="bg-green-600 h-3 rounded-full transition-all duration-300"
                          style={{ width: `${jobProgress}%` }}
                        />
                      </div>
                      <p className="text-xs mt-1 text-green-700">{jobMessage}</p>
                    </div>
                  )}
                  
                  {/* Result display */}
                  {updateDetailsResult && (
                    <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded text-sm">
                      <p className="font-bold text-green-600">{updateDetailsResult.message}</p>
                      <div className="mt-2 text-xs grid grid-cols-2 gap-2">
                        <p>Total in Excel: {updateDetailsResult.total_students_in_excel}</p>
                        <p>Found in DB: {updateDetailsResult.students_found}</p>
                        <p className="text-green-600 font-medium">Updated: {updateDetailsResult.students_updated}</p>
                        <p className="text-orange-600">Not Found: {updateDetailsResult.students_not_found}</p>
                      </div>
                      
                      {/* By class breakdown */}
                      {updateDetailsResult.by_class && Object.keys(updateDetailsResult.by_class).length > 0 && (
                        <div className="mt-2 border-t pt-2">
                          <p className="font-medium text-xs mb-1">Class-wise breakdown:</p>
                          <div className="text-xs max-h-32 overflow-auto">
                            {Object.entries(updateDetailsResult.by_class).map(([className, data]) => (
                              <div key={className} className="flex justify-between py-0.5 border-b border-gray-100">
                                <span>{className} → {data.current_class}</span>
                                <span className="text-green-600">{data.updated}/{data.students}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Not found list */}
                      {updateDetailsResult.not_found_list && updateDetailsResult.not_found_list.length > 0 && (
                        <div className="mt-2 border-t pt-2">
                          <p className="font-medium text-xs mb-1 text-orange-600">Not Found ({updateDetailsResult.not_found_list.length}):</p>
                          <div className="text-xs max-h-24 overflow-auto bg-orange-50 p-1 rounded">
                            {updateDetailsResult.not_found_list.slice(0, 10).map((item, idx) => (
                              <div key={idx} className="text-orange-700">{item.name} ({item.expected_class})</div>
                            ))}
                            {updateDetailsResult.not_found_list.length > 10 && (
                              <div className="text-orange-500">...and {updateDetailsResult.not_found_list.length - 10} more</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Compare students tool */}
                  <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-200">
                    <p className="text-xs font-medium text-blue-700 mb-2">🔍 Database এ কোন students আছে দেখুন:</p>
                    <div className="flex gap-2 flex-wrap">
                      {['Class 2', 'Class 3', 'Class 4', 'Class 5', 'Class 6'].map(cls => (
                        <Button 
                          key={cls}
                          variant="outline" 
                          size="sm"
                          onClick={() => compareStudentsWithExcel(cls)}
                          className="text-xs"
                        >
                          {cls}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* WRONG INVOICE CLEANUP */}
                <div className="p-4 border-4 border-red-600 rounded bg-red-50 dark:bg-red-900/30" data-testid="wrong-invoice-cleanup-section">
                  <h5 className="font-bold text-red-700 text-lg mb-2">WRONG INVOICE CLEANUP</h5>
                  <p className="text-sm text-muted-foreground mb-3">
                    Re-admission / Promotion students এর জন্য ভুল fee invoices (যেমন Admission Fee) detect ও delete করুন।
                    শুধু unpaid invoices delete হবে, paid invoices skip হবে।
                  </p>

                  <div className="flex gap-2 items-end flex-wrap mb-3">
                    <div>
                      <label className="text-xs font-medium mb-1 block">Date Filter (optional)</label>
                      <Input
                        type="date"
                        value={wrongInvoiceDateFilter}
                        onChange={(e) => setWrongInvoiceDateFilter(e.target.value)}
                        className="w-44"
                        data-testid="wrong-invoice-date-filter"
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={previewWrongInvoices}
                      disabled={loadingWrongInvoices || !selectedSchool}
                      className="border-red-500 text-red-700"
                      data-testid="scan-wrong-invoices-btn"
                    >
                      {loadingWrongInvoices ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                      Scan Wrong Invoices
                    </Button>
                  </div>

                  {wrongInvoicePreview && (
                    <div className="bg-white dark:bg-gray-800 p-3 rounded mb-3 text-sm" data-testid="wrong-invoice-preview">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                        <div className="p-2 bg-red-100 rounded text-center">
                          <p className="text-2xl font-bold text-red-600">{wrongInvoicePreview.total_wrong}</p>
                          <p className="text-xs">Wrong Invoices</p>
                        </div>
                        <div className="p-2 bg-amber-100 rounded text-center">
                          <p className="text-2xl font-bold text-amber-600">{wrongInvoicePreview.total_amount?.toLocaleString()}</p>
                          <p className="text-xs">Total Amount</p>
                        </div>
                        <div className="p-2 bg-green-100 rounded text-center">
                          <p className="text-2xl font-bold text-green-600">{wrongInvoicePreview.deletable}</p>
                          <p className="text-xs">Deletable (Unpaid)</p>
                        </div>
                        <div className="p-2 bg-gray-100 rounded text-center">
                          <p className="text-2xl font-bold text-gray-600">{wrongInvoicePreview.has_payments_cant_delete}</p>
                          <p className="text-xs">Paid (Skipped)</p>
                        </div>
                      </div>

                      {Object.keys(wrongInvoicePreview.by_student_summary || {}).length > 0 && (
                        <div className="mb-3">
                          <p className="font-medium text-xs mb-1">Student-wise Summary:</p>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(wrongInvoicePreview.by_student_summary).map(([name, info]) => (
                              <Badge key={name} variant="outline" className="bg-red-50 text-red-700">
                                {name} ({info.admission_type}): {info.count} invoices, Rs.{info.amount?.toLocaleString()}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {wrongInvoicePreview.wrong_invoices?.length > 0 && (
                        <div className="max-h-48 overflow-auto bg-gray-50 p-2 rounded text-xs mb-3">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b font-medium">
                                <th className="text-left p-1">Student</th>
                                <th className="text-left p-1">Fee</th>
                                <th className="text-right p-1">Amt</th>
                                <th className="text-left p-1">Status</th>
                                <th className="text-left p-1">Reason</th>
                              </tr>
                            </thead>
                            <tbody>
                              {wrongInvoicePreview.wrong_invoices.map((inv, idx) => (
                                <tr key={idx} className="border-b">
                                  <td className="p-1">{inv.student_name}</td>
                                  <td className="p-1">{inv.fee_head_name}</td>
                                  <td className="p-1 text-right">{inv.amount}</td>
                                  <td className="p-1">
                                    <Badge variant={inv.has_payment ? "default" : "destructive"} className="text-[10px]">
                                      {inv.has_payment ? 'PAID' : inv.status}
                                    </Badge>
                                  </td>
                                  <td className="p-1 text-gray-500">{inv.reason}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {wrongInvoicePreview.deletable > 0 && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={deleteWrongInvoices}
                          disabled={deletingWrongInvoices}
                          data-testid="delete-wrong-invoices-btn"
                        >
                          {deletingWrongInvoices ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                          DELETE {wrongInvoicePreview.deletable} WRONG INVOICES
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {/* CRON DUPLICATE CLEANUP */}
                <div className="p-4 border-4 border-orange-600 rounded bg-orange-50 dark:bg-orange-900/30" data-testid="cron-duplicate-cleanup-section">
                  <h5 className="font-bold text-orange-700 text-lg mb-2">CRON DUPLICATE CLEANUP (One-Time Fees)</h5>
                  <p className="text-sm text-muted-foreground mb-3">
                    Cron job দ্বারা ভুল generate হওয়া one-time fee invoices (Session Fee, Computer Fee, etc.) delete করুন।
                    শুধু নির্দিষ্ট মাসের unpaid duplicate invoices delete হবে।
                  </p>

                  <div className="flex gap-2 items-end flex-wrap mb-3">
                    <div>
                      <label className="text-xs font-medium mb-1 block">Month (YYYY-MM) *</label>
                      <Input
                        type="month"
                        value={cronDupMonth}
                        onChange={(e) => setCronDupMonth(e.target.value)}
                        className="w-44"
                        data-testid="cron-dup-month-input"
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={previewCronDuplicates}
                      disabled={loadingCronDup || !selectedSchool || !cronDupMonth}
                      className="border-orange-500 text-orange-700"
                      data-testid="scan-cron-duplicates-btn"
                    >
                      {loadingCronDup ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                      Scan Duplicates
                    </Button>
                  </div>

                  {cronDupPreview && (
                    <div className="bg-white dark:bg-gray-800 p-3 rounded mb-3 text-sm" data-testid="cron-dup-preview">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
                        <div className="p-2 bg-orange-100 rounded text-center">
                          <p className="text-2xl font-bold text-orange-600">{cronDupPreview.total}</p>
                          <p className="text-xs">Duplicates Found</p>
                        </div>
                        <div className="p-2 bg-amber-100 rounded text-center">
                          <p className="text-2xl font-bold text-amber-600">{cronDupPreview.total_amount?.toLocaleString()}</p>
                          <p className="text-xs">Total Amount</p>
                        </div>
                        <div className="p-2 bg-green-100 rounded text-center">
                          <p className="text-2xl font-bold text-green-600">{cronDupPreview.deletable}</p>
                          <p className="text-xs">Deletable (Unpaid)</p>
                        </div>
                      </div>

                      {Object.keys(cronDupPreview.by_fee_summary || {}).length > 0 && (
                        <div className="mb-3">
                          <p className="font-medium text-xs mb-1">Fee-wise Summary:</p>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(cronDupPreview.by_fee_summary).map(([name, info]) => (
                              <Badge key={name} variant="outline" className="bg-orange-50 text-orange-700">
                                {name}: {info.count} invoices, Rs.{info.amount?.toLocaleString()}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {cronDupPreview.deletable > 0 && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={deleteCronDuplicates}
                          disabled={deletingCronDup}
                          data-testid="delete-cron-duplicates-btn"
                        >
                          {deletingCronDup ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                          DELETE {cronDupPreview.deletable} DUPLICATES
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {/* ONE-TIME FEE DUPLICATE CLEANUP */}
                <div className="p-4 border-4 border-purple-600 rounded bg-purple-100 dark:bg-purple-900/50">
                  <h5 className="font-bold text-purple-700 text-lg mb-2">One-Time Fee Duplicates (Promotion Bug)</h5>
                  <p className="text-sm text-muted-foreground mb-3">
                    Finds one-time fees (Admission, Registration, etc.) duplicated across academic years due to promotion. Only unpaid duplicates will be deleted.
                  </p>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={previewOnetimeDuplicates}
                    disabled={loadingOnetimeDup}
                    className="border-purple-500 text-purple-700"
                    data-testid="scan-onetime-duplicates-btn"
                  >
                    {loadingOnetimeDup ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                    Scan for Duplicates
                  </Button>

                  {onetimeDupPreview && onetimeDupPreview.stats && (
                    <div className="mt-3 space-y-3">
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="bg-white dark:bg-gray-800 p-2 rounded">
                          <p className="text-2xl font-bold text-purple-600">{onetimeDupPreview.stats.total_duplicates}</p>
                          <p className="text-xs text-muted-foreground">Total Duplicates</p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-2 rounded">
                          <p className="text-2xl font-bold text-green-600">{onetimeDupPreview.stats.deletable_unpaid}</p>
                          <p className="text-xs text-muted-foreground">Deletable (Unpaid)</p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-2 rounded">
                          <p className="text-2xl font-bold text-orange-600">{onetimeDupPreview.stats.affected_students}</p>
                          <p className="text-xs text-muted-foreground">Affected Students</p>
                        </div>
                      </div>

                      {onetimeDupPreview.duplicates?.length > 0 && (
                        <div className="max-h-48 overflow-y-auto bg-white dark:bg-gray-800 rounded p-2 text-xs">
                          <table className="w-full">
                            <thead>
                              <tr className="text-left border-b">
                                <th className="pb-1">Student</th>
                                <th className="pb-1">Fee Head</th>
                                <th className="pb-1">Amount</th>
                                <th className="pb-1">Status</th>
                                <th className="pb-1">Year</th>
                              </tr>
                            </thead>
                            <tbody>
                              {onetimeDupPreview.duplicates.map((d, i) => (
                                <tr key={i} className="border-b border-gray-100">
                                  <td className="py-1">{d.student_name}</td>
                                  <td>{d.fee_head_name}</td>
                                  <td>₹{d.amount?.toLocaleString()}</td>
                                  <td className={d.is_deletable ? 'text-green-600' : 'text-red-600'}>
                                    {d.status} {d.paid_amount > 0 ? `(₹${d.paid_amount})` : ''}
                                  </td>
                                  <td>{d.academic_year}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {onetimeDupPreview.stats.deletable_unpaid > 0 && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={deleteOnetimeDuplicates}
                          disabled={deletingOnetimeDup}
                          data-testid="delete-onetime-duplicates-btn"
                        >
                          {deletingOnetimeDup ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                          DELETE {onetimeDupPreview.stats.deletable_unpaid} DUPLICATES (₹{onetimeDupPreview.stats.total_amount_deletable?.toLocaleString()})
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {/* WRONG ADMISSION FEES FOR RE-ADMISSION/PROMOTION STUDENTS */}
                <div className="p-4 border-4 border-red-600 rounded bg-red-100 dark:bg-red-900/50">
                  <h5 className="font-bold text-red-700 text-lg mb-2">Wrong Admission Fees (Re-admission/Promotion Bug)</h5>
                  <p className="text-sm text-muted-foreground mb-3">
                    Finds Admission Fee & Admission Form invoices wrongly generated for re-admission/promotion students. These students should NOT have admission fees.
                  </p>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={previewWrongAdmFees}
                    disabled={loadingWrongAdm}
                    className="border-red-500 text-red-700"
                    data-testid="scan-wrong-adm-fees-btn"
                  >
                    {loadingWrongAdm ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                    Scan Wrong Admission Fees
                  </Button>

                  {wrongAdmPreview && wrongAdmPreview.stats && (
                    <div className="mt-3 space-y-3">
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="bg-white dark:bg-gray-800 p-2 rounded">
                          <p className="text-2xl font-bold text-red-600">{wrongAdmPreview.stats.total_wrong}</p>
                          <p className="text-xs text-muted-foreground">Wrong Invoices</p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-2 rounded">
                          <p className="text-2xl font-bold text-green-600">{wrongAdmPreview.stats.deletable_unpaid}</p>
                          <p className="text-xs text-muted-foreground">Deletable (Unpaid)</p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-2 rounded">
                          <p className="text-2xl font-bold text-orange-600">{wrongAdmPreview.stats.affected_students}</p>
                          <p className="text-xs text-muted-foreground">Affected Students</p>
                        </div>
                      </div>

                      {wrongAdmPreview.wrong_invoices?.length > 0 && (
                        <div className="max-h-48 overflow-y-auto bg-white dark:bg-gray-800 rounded p-2 text-xs">
                          <table className="w-full">
                            <thead>
                              <tr className="text-left border-b">
                                <th className="pb-1">Student</th>
                                <th className="pb-1">Adm. Type</th>
                                <th className="pb-1">Fee</th>
                                <th className="pb-1">Amount</th>
                                <th className="pb-1">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {wrongAdmPreview.wrong_invoices.map((d, i) => (
                                <tr key={i} className="border-b border-gray-100">
                                  <td className="py-1">{d.student_name}</td>
                                  <td className="uppercase text-xs">{d.admission_type}</td>
                                  <td>{d.fee_head_name}</td>
                                  <td>₹{d.amount?.toLocaleString()}</td>
                                  <td className={d.is_deletable ? 'text-green-600' : 'text-red-600'}>
                                    {d.status} {d.paid_amount > 0 ? `(Paid ₹${d.paid_amount})` : ''}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {wrongAdmPreview.stats.deletable_unpaid > 0 && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={deleteWrongAdmFees}
                          disabled={deletingWrongAdm}
                          data-testid="delete-wrong-adm-fees-btn"
                        >
                          {deletingWrongAdm ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                          DELETE {wrongAdmPreview.stats.deletable_unpaid} WRONG INVOICES (₹{wrongAdmPreview.stats.total_amount_deletable?.toLocaleString()})
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {/* FRACTIONAL AMOUNTS CLEANUP */}
                <div className="p-4 border-4 border-amber-600 rounded bg-amber-50 dark:bg-amber-900/20 mt-4">
                  <h5 className="font-bold text-amber-800 text-lg mb-2">Fractional Amount Cleanup (Ledger Rs. 1966.67 bug)</h5>
                  <p className="text-sm text-muted-foreground mb-3">
                    Finds invoices & payments that contain fractional (non-integer) amounts like Rs. 1966.67 caused by the old proportional discount split.
                    Rounds each fractional field to the nearest whole rupee. Optional: provide a student ID to scope to one student.
                  </p>

                  <div className="flex gap-2 mb-3">
                    <Input
                      type="text"
                      placeholder="Student name / admission no. / ID (optional)"
                      value={fracStudentId}
                      onChange={(e) => setFracStudentId(e.target.value.trim())}
                      className="max-w-xs"
                      data-testid="frac-student-id-input"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={scanFractionalAmounts}
                      disabled={loadingFracScan}
                      className="border-amber-600 text-amber-800"
                      data-testid="scan-fractional-amounts-btn"
                    >
                      {loadingFracScan ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                      Scan Fractional Amounts
                    </Button>
                  </div>

                  {fracScan && (
                    <div className="mt-3 space-y-3">
                      <div className="grid grid-cols-2 gap-2 text-center">
                        <div className="bg-white dark:bg-gray-800 p-2 rounded">
                          <p className="text-2xl font-bold text-amber-700">{fracScan.invoices_with_fractions}</p>
                          <p className="text-xs text-muted-foreground">Invoices w/ Fractions</p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-2 rounded">
                          <p className="text-2xl font-bold text-amber-700">{fracScan.payments_with_fractions}</p>
                          <p className="text-xs text-muted-foreground">Payments w/ Fractions</p>
                        </div>
                      </div>

                      {(fracScan.invoices?.length > 0 || fracScan.payments?.length > 0) && (
                        <div className="max-h-64 overflow-y-auto bg-white dark:bg-gray-800 rounded p-2 text-xs">
                          <table className="w-full">
                            <thead>
                              <tr className="text-left border-b">
                                <th className="pb-1">Type</th>
                                <th className="pb-1">Reference</th>
                                <th className="pb-1">Fractional Fields</th>
                              </tr>
                            </thead>
                            <tbody>
                              {fracScan.invoices?.slice(0, 50).map((inv, i) => (
                                <tr key={`i-${i}`} className="border-b border-gray-100">
                                  <td className="py-1">Invoice</td>
                                  <td>{inv.invoice_number || inv.id?.slice(0, 8)}</td>
                                  <td className="font-mono">{Object.entries(inv.fields).map(([k, v]) => `${k}=${v}`).join(', ')}</td>
                                </tr>
                              ))}
                              {fracScan.payments?.slice(0, 50).map((p, i) => (
                                <tr key={`p-${i}`} className="border-b border-gray-100">
                                  <td className="py-1">Payment</td>
                                  <td>{p.receipt_number || p.id?.slice(0, 8)}</td>
                                  <td className="font-mono">{Object.entries(p.fields).map(([k, v]) => `${k}=${v}`).join(', ')}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {((fracScan.invoices_with_fractions || 0) + (fracScan.payments_with_fractions || 0)) > 0 && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={fixFractionalAmounts}
                          disabled={fixingFrac}
                          data-testid="fix-fractional-amounts-btn"
                        >
                          {fixingFrac ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                          ROUND ALL TO NEAREST RUPEE ({(fracScan.invoices_with_fractions || 0) + (fracScan.payments_with_fractions || 0)} records)
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {/* DUPLICATE ONE-TIME FEE CLEANUP */}
                <div className="p-4 border-4 border-orange-600 rounded bg-orange-50 dark:bg-orange-900/20 mt-4">
                  <h5 className="font-bold text-orange-800 text-lg mb-2">Duplicate One-Time Fees Cleanup (Session/Dev/Exam/Computer etc.)</h5>
                  <p className="text-sm text-muted-foreground mb-3">
                    Finds duplicate invoices for ONE-TIME fee heads (Session Fee, Development Fee, Exam Fee, Computer Fee, Library Fee, Yoga, etc.)
                    that were created multiple times for the same student in the same academic year.
                    Keeps the earliest PAID invoice (or earliest if none paid) and deletes the UNPAID duplicates.
                    Optional: scope to one student by name / admission no. / ID.
                  </p>

                  <div className="flex gap-2 mb-3">
                    <Input
                      type="text"
                      placeholder="Student ID (optional - leave blank for all)"
                      value={dupOneTimeStudentId}
                      onChange={(e) => setDupOneTimeStudentId(e.target.value.trim())}
                      className="max-w-xs"
                      data-testid="dup-onetime-student-id-input"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={scanDupOneTime}
                      disabled={loadingDupOneTime}
                      className="border-orange-600 text-orange-800"
                      data-testid="scan-dup-onetime-btn"
                    >
                      {loadingDupOneTime ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                      Scan Duplicate One-Time Fees
                    </Button>
                  </div>

                  {dupOneTimePreview && (
                    <div className="mt-3 space-y-3">
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="bg-white dark:bg-gray-800 p-2 rounded">
                          <p className="text-2xl font-bold text-orange-700">{dupOneTimePreview.stats?.total_duplicate_groups || 0}</p>
                          <p className="text-xs text-muted-foreground">Duplicate Groups</p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-2 rounded">
                          <p className="text-2xl font-bold text-red-700">{dupOneTimePreview.stats?.total_deletable || 0}</p>
                          <p className="text-xs text-muted-foreground">Deletable Invoices</p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-2 rounded">
                          <p className="text-2xl font-bold text-green-700">₹{(dupOneTimePreview.stats?.total_deletable_amount || 0).toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground">Total Wrong Billing</p>
                        </div>
                      </div>

                      {dupOneTimePreview.duplicate_groups?.length > 0 && (
                        <div className="max-h-72 overflow-y-auto bg-white dark:bg-gray-800 rounded p-2 text-xs">
                          <table className="w-full">
                            <thead className="sticky top-0 bg-orange-100 dark:bg-orange-900">
                              <tr className="text-left">
                                <th className="pb-1 pr-2">Student</th>
                                <th className="pb-1 pr-2">Class</th>
                                <th className="pb-1 pr-2">Fee</th>
                                <th className="pb-1 pr-2">Yr</th>
                                <th className="pb-1 pr-2">Keep</th>
                                <th className="pb-1 pr-2">Delete</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dupOneTimePreview.duplicate_groups.slice(0, 200).map((g, i) => (
                                <tr key={i} className="border-b border-gray-100">
                                  <td className="py-1 pr-2">{g.student_name}</td>
                                  <td className="pr-2">{g.class_name}</td>
                                  <td className="pr-2">{g.fee_head_name}</td>
                                  <td className="pr-2">{g.academic_year}</td>
                                  <td className="pr-2 text-green-700">
                                    {g.keeper?.month} ({g.keeper?.status}) ₹{g.keeper?.amount}
                                  </td>
                                  <td className="pr-2 text-red-700">
                                    {g.duplicates_deletable?.map(d => `${d.month}=₹${d.amount}`).join(', ')}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {(dupOneTimePreview.stats?.total_deletable || 0) > 0 && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={deleteDupOneTime}
                          disabled={deletingDupOneTime}
                          data-testid="delete-dup-onetime-btn"
                        >
                          {deletingDupOneTime ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                          DELETE {dupOneTimePreview.stats.total_deletable} DUPLICATE INVOICES (₹{(dupOneTimePreview.stats.total_deletable_amount || 0).toLocaleString()})
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {/* ACCOUNTING DATA CLEANUP */}
                <div className="p-4 border-4 border-indigo-600 rounded bg-indigo-100 dark:bg-indigo-900/50">
                  <h5 className="font-bold text-indigo-700 text-lg mb-2">💰 ACCOUNTING DATA CLEANUP</h5>
                  <p className="text-sm text-muted-foreground mb-3">
                    Test/Dummy accounting entries delete করুন। Payment করার পর accounting এ entry হচ্ছে কিনা verify করুন।
                  </p>
                  
                  <div className="flex gap-2 flex-wrap mb-3">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={fetchAccountingSummary}
                      disabled={loadingAccountingSummary}
                      className="border-indigo-500 text-indigo-700"
                      data-testid="view-accounting-summary-btn"
                    >
                      {loadingAccountingSummary ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
                      View Accounting Summary
                    </Button>
                  </div>
                  
                  {/* Accounting Summary Display */}
                  {accountingSummary && (
                    <div className="bg-white dark:bg-gray-800 p-3 rounded mb-3 text-sm">
                      <h6 className="font-bold text-indigo-600 mb-2">📊 Accounting Data Summary</h6>
                      
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
                        <div className="p-2 bg-indigo-50 rounded text-center">
                          <p className="text-2xl font-bold text-indigo-600">{accountingSummary.journal_entries?.total || 0}</p>
                          <p className="text-xs text-gray-600">Journal Entries</p>
                        </div>
                        <div className="p-2 bg-green-50 rounded text-center">
                          <p className="text-2xl font-bold text-green-600">{accountingSummary.accounting_entries?.total || 0}</p>
                          <p className="text-xs text-gray-600">Fee Accounting</p>
                        </div>
                        <div className="p-2 bg-blue-50 rounded text-center">
                          <p className="text-2xl font-bold text-blue-600">{accountingSummary.income_entries?.total || 0}</p>
                          <p className="text-xs text-gray-600">Income Entries</p>
                        </div>
                        <div className="p-2 bg-red-50 rounded text-center">
                          <p className="text-2xl font-bold text-red-600">{accountingSummary.expense_entries?.total || 0}</p>
                          <p className="text-xs text-gray-600">Expense Entries</p>
                        </div>
                        <div className="p-2 bg-purple-50 rounded text-center">
                          <p className="text-2xl font-bold text-purple-600">{accountingSummary.transactions?.total || 0}</p>
                          <p className="text-xs text-gray-600">Transactions</p>
                        </div>
                      </div>
                      
                      {/* Income by category */}
                      {accountingSummary.income_entries?.by_category && Object.keys(accountingSummary.income_entries.by_category).length > 0 && (
                        <div className="mb-3">
                          <p className="font-medium text-xs mb-1">💵 Income by Category:</p>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(accountingSummary.income_entries.by_category).map(([cat, data]) => (
                              <Badge key={cat} variant="outline" className="bg-green-50">
                                {cat}: ₹{data.total?.toLocaleString()} ({data.count})
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Recent samples */}
                      {accountingSummary.income_entries?.samples?.length > 0 && (
                        <div className="text-xs">
                          <p className="font-medium mb-1">Recent Income Entries:</p>
                          <div className="max-h-24 overflow-auto bg-gray-50 p-2 rounded">
                            {accountingSummary.income_entries.samples.map((entry, idx) => (
                              <div key={idx} className="border-b pb-1 mb-1">
                                ₹{entry.amount?.toLocaleString()} - {entry.category || 'N/A'} - {entry.description?.slice(0, 50)}...
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Delete Options */}
                  <div className="flex items-center gap-3 mb-3">
                    <label className="text-sm font-medium">Delete Type:</label>
                    <Select value={accountingDeleteType} onValueChange={setAccountingDeleteType}>
                      <SelectTrigger className="w-48" data-testid="accounting-delete-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="test">Test/Dummy Data Only</SelectItem>
                        <SelectItem value="all">⚠️ ALL Data</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={handleDeleteAccountingData}
                    disabled={deletingAccountingData}
                    className="bg-indigo-800 hover:bg-indigo-900"
                    data-testid="delete-accounting-data-btn"
                  >
                    {deletingAccountingData ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                    DELETE ACCOUNTING DATA
                  </Button>
                  
                  <p className="text-xs text-indigo-600 mt-2">
                    💡 "Test/Dummy Data Only" selects entries with no student_id or containing "test", "dummy", "sample" in description.
                  </p>
                </div>

                {/* EMERGENCY FIX - For specific students */}
                <div className="p-3 border-2 border-orange-500 rounded bg-orange-50 dark:bg-orange-950/30">
                  <h5 className="font-bold text-orange-700 mb-2">🚨 Emergency Fix Students (নির্দিষ্ট students)</h5>
                  <p className="text-xs text-muted-foreground mb-3">
                    Move specific students to CORRECT class and DELETE all their invoices, fee assignments, and promotion history.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                    <div>
                      <label className="text-xs font-medium mb-1 block">Correct Class (Move To)</label>
                      <Select value={emergencyFixClassId} onValueChange={setEmergencyFixClassId}>
                        <SelectTrigger data-testid="emergency-fix-class-selector">
                          <SelectValue placeholder="Select CORRECT class..." />
                        </SelectTrigger>
                        <SelectContent>
                          {schoolClasses.map(cls => (
                            <SelectItem key={cls.id} value={cls.id}>
                              {cls.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label className="text-xs font-medium mb-1 block">Admission Numbers (one per line)</label>
                      <textarea
                        placeholder="MSMS2025-20260572&#10;MSMS2025-20260573"
                        value={emergencyFixNumbers}
                        onChange={(e) => setEmergencyFixNumbers(e.target.value)}
                        className="w-full h-20 p-2 border rounded text-xs font-mono"
                        data-testid="emergency-fix-numbers-input"
                      />
                    </div>
                  </div>
                  
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={emergencyFix}
                    disabled={emergencyFixing || !emergencyFixClassId}
                    className="bg-orange-600 hover:bg-orange-700"
                    data-testid="emergency-fix-btn"
                  >
                    {emergencyFixing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <AlertTriangle className="h-4 w-4 mr-2" />}
                    EMERGENCY FIX (Move + Clean Data)
                  </Button>
                  
                  {emergencyFixResult && (
                    <div className="mt-3 p-2 bg-white dark:bg-gray-800 rounded text-sm">
                      <p className="font-medium text-green-600">{emergencyFixResult.message}</p>
                      {emergencyFixResult.fixed && emergencyFixResult.fixed.length > 0 && (
                        <ul className="text-xs mt-1">
                          {emergencyFixResult.fixed.map((s, i) => (
                            <li key={i}>✓ {s.name} ({s.student_id}): {s.from_class} → {s.to_class}</li>
                          ))}
                        </ul>
                      )}
                      {emergencyFixResult.not_found && emergencyFixResult.not_found.length > 0 && (
                        <p className="text-xs text-amber-600 mt-1">Not found: {emergencyFixResult.not_found.join(', ')}</p>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Fix Student Classes */}
                <div className="flex items-center gap-4">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={fixStudentClasses}
                    disabled={fixingClasses}
                    className="border-amber-500 text-amber-700 hover:bg-amber-100"
                    data-testid="fix-student-classes-btn"
                  >
                    {fixingClasses ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                    Fix Student Classes (from history)
                  </Button>
                  <span className="text-xs text-muted-foreground">Updates class_id for students based on promotion history</span>
                </div>
                
                {/* Reset All Promotions */}
                <div className="flex flex-wrap items-center gap-4">
                  <Input
                    placeholder="Academic Year (e.g., 2026-2027)"
                    value={resetAcademicYear}
                    onChange={(e) => setResetAcademicYear(e.target.value)}
                    className="w-48"
                    data-testid="reset-academic-year-input"
                  />
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={resetAllPromotions}
                    disabled={resettingPromotions}
                    data-testid="reset-all-promotions-btn"
                  >
                    {resettingPromotions ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                    RESET ALL PROMOTIONS
                  </Button>
                </div>
                <p className="text-xs text-red-600 font-medium">
                  ⚠️ RESET ALL PROMOTIONS will: Revert students to original class, DELETE all invoices & fee assignments for that year
                </p>
              </div>
            </div>

            {/* Reset Type */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Reset Type</label>
              <div className="flex gap-4">
                <Button
                  variant={resetType === 'all' ? 'destructive' : 'outline'}
                  onClick={() => setResetType('all')}
                  data-testid="reset-type-all"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Reset All Data
                </Button>
                <Button
                  variant={resetType === 'partial' ? 'default' : 'outline'}
                  onClick={() => setResetType('partial')}
                  data-testid="reset-type-partial"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Partial Reset
                </Button>
              </div>
            </div>

            {/* Collection Selection (for partial reset) */}
            {resetType === 'partial' && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">Select collections to reset:</p>
                
                {Object.entries(COLLECTION_GROUPS).map(([group, collections]) => (
                  <div key={group} className="space-y-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => selectGroup(group)}
                      className="font-medium"
                    >
                      {group}
                    </Button>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 pl-4">
                      {collections.map(collection => (
                        <div
                          key={collection}
                          className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                            selectedCollections.includes(collection)
                              ? 'bg-red-50 dark:bg-red-950 border-red-300'
                              : 'hover:bg-muted'
                          }`}
                          onClick={() => toggleCollection(collection)}
                        >
                          <Checkbox
                            checked={selectedCollections.includes(collection)}
                            onChange={() => toggleCollection(collection)}
                          />
                          {getCollectionIcon(collection)}
                          <span className="text-sm">{collection}</span>
                          <Badge variant="secondary" className="ml-auto">
                            {dataSummary.collections[collection] || 0}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Reset Button */}
            <div className="flex gap-2 pt-4">
              <Button
                variant="destructive"
                onClick={() => setShowConfirmDialog(true)}
                disabled={resetType === 'partial' && selectedCollections.length === 0}
                data-testid="reset-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {resetType === 'all' ? 'Reset All Data' : `Reset ${selectedCollections.length} Collections`}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowHistory(!showHistory)}
              >
                <History className="h-4 w-4 mr-2" />
                Reset History
              </Button>
            </div>

            {/* Reset History */}
            {showHistory && resetHistory.length > 0 && (
              <div className="border rounded-lg p-4 space-y-2">
                <h4 className="font-medium">Reset History</h4>
                {resetHistory.map((log, idx) => (
                  <div key={idx} className="text-sm p-2 bg-muted rounded">
                    <p>{new Date(log.performed_at).toLocaleString()}</p>
                    <p className="text-muted-foreground">
                      Type: {log.reset_type} | Collections: {log.collections_reset?.join(', ')}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ======= DELETE TENANT SECTION ======= */}
        {selectedSchool && (
          <div className="border-2 border-red-500 rounded-lg p-4 mt-6 bg-red-50/50 dark:bg-red-950/20">
            <h4 className="font-bold text-red-600 text-lg flex items-center gap-2 mb-2">
              <AlertTriangle className="h-5 w-5" />
              DELETE TENANT (Permanent)
            </h4>
            <p className="text-sm text-muted-foreground mb-4">
              Permanently delete this school and ALL its data — students, fees, payments, accounting, users, everything. This cannot be undone.
            </p>
            
            <Button
              variant="destructive"
              onClick={previewDeleteTenant}
              disabled={deletingTenant}
              data-testid="preview-delete-tenant-btn"
            >
              {deletingTenant ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Preview Delete
            </Button>

            {deleteTenantPreview && deleteTenantPreview.status === 'preview' && (
              <div className="mt-4 space-y-3">
                <div className="p-3 bg-white dark:bg-gray-800 rounded border">
                  <p className="font-medium text-red-600">{deleteTenantPreview.school?.name} ({deleteTenantPreview.school?.school_code})</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Total Records: <strong>{deleteTenantPreview.total_records?.toLocaleString()}</strong> | 
                    Users: <strong>{deleteTenantPreview.user_count}</strong>
                  </p>
                </div>
                
                {deleteTenantPreview.collections_with_data && Object.keys(deleteTenantPreview.collections_with_data).length > 0 && (
                  <div className="p-3 bg-white dark:bg-gray-800 rounded border text-sm max-h-40 overflow-y-auto">
                    <p className="font-medium mb-1">Data breakdown:</p>
                    {Object.entries(deleteTenantPreview.collections_with_data).map(([key, count]) => (
                      <p key={key} className="text-muted-foreground">{key}: {count}</p>
                    ))}
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-medium text-red-600">
                    Type the exact school name to confirm: <strong>{deleteTenantPreview.school?.name}</strong>
                  </label>
                  <input
                    type="text"
                    value={deleteTenantConfirmText}
                    onChange={(e) => setDeleteTenantConfirmText(e.target.value)}
                    className="w-full p-2 border border-red-300 rounded"
                    placeholder={deleteTenantPreview.school?.name}
                    data-testid="delete-tenant-confirm-input"
                  />
                </div>

                <Button
                  variant="destructive"
                  onClick={executeDeleteTenant}
                  disabled={deletingTenant || deleteTenantConfirmText !== deleteTenantPreview.school?.name}
                  className="w-full"
                  data-testid="execute-delete-tenant-btn"
                >
                  {deletingTenant ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                  PERMANENTLY DELETE "{deleteTenantPreview.school?.name}"
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Confirmation Dialog */}
        <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Confirm Data Reset
              </DialogTitle>
              <DialogDescription>
                This action will PERMANENTLY DELETE data and cannot be undone!
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-4 bg-red-50 dark:bg-red-950 rounded-lg">
                <p className="font-medium">{dataSummary?.school?.name}</p>
                <p className="text-sm text-muted-foreground">
                  {resetType === 'all' 
                    ? `All data (${dataSummary?.total_records} records) will be deleted`
                    : `${selectedCollections.length} collections will be reset`
                  }
                </p>
              </div>
              <div>
                <label className="text-sm font-medium">Type DELETE to confirm:</label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  className="w-full mt-1 p-2 border rounded"
                  placeholder="DELETE"
                  data-testid="confirm-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleReset}
                disabled={confirmText !== 'DELETE' || loading}
                data-testid="confirm-reset-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Permanently Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default TenantResetManager;
